/**
 * Inglês — a fonte.
 *
 * Idioma padrão do produto, e a rede de segurança dos outros dois: quando uma
 * chave falta em `pt` ou `es`, é este texto que aparece. Uma entrada que exista
 * só aqui degrada para inglês legível em vez de mostrar o identificador.
 *
 * Plural é `{ um, outros }` e é escolhido pela variável `n`.
 */
const en = {
  comum: {
    salvar: 'Save changes',
    salvando: 'Saving…',
    cancelar: 'Cancel',
    fechar: 'Close',
    pularConteudo: 'Skip to content',
    carregando: 'Loading…',
    soGestor: 'Only a company manager can change this.',
    voltar: 'Back',
    buscar: 'Search',
    todos: 'All',
    ordenarPor: 'Sort by',
    ordemPadrao: 'Default order',
    crescente: 'ascending',
    decrescente: 'descending'
  },

  menu: {
    visaoGeral: 'Overview',
    painel: 'Dashboard',
    projetos: 'Projects',
    meusProjetos: 'My Projects',
    criarProjeto: 'Create Project',
    conta: 'Account',
    perfil: 'My Profile',
    pagamentos: 'Payments',
    planoEUso: 'Plan and Usage',
    legal: 'Legal',
    privacidade: 'Privacy Policy',
    termos: 'Terms of Service',
    sair: 'Sign out',
    creditosSufixo: '/{total} AI credits',
    faturamento: 'Billing',
    configuracoes: 'Settings',
    planos: 'Plans',
    execucoes: 'Runs',
    ajuda: 'Help',
    documentacao: 'Documentation',
    creditos: '{restantes}/{total} AI credits'
  },

  conta: {
    titulo: 'Account',
    plano: '{plano} plan',
    verPlanos: 'See all plans',

    perfil: 'Profile',
    nome: 'Name',
    email: 'Email',
    trocarFoto: 'Change',
    enviandoFoto: 'Uploading…',
    perfilSalvo: 'Profile updated.',
    fotoSalva: 'Profile photo updated.',

    empresaTexto: 'Your company details. Only a manager can change them.',
    freelancerTexto:
      "Your account is for one person: the plan, AI credits and projects are yours alone. To turn it into a company, fill in the details. The plan, credits and projects stay as they are, and you can't go back to freelancer.",
    soGestor: 'Only a manager can change the company details.',
    converter: 'Convert to company',
    confirmarConversao: "Convert this account to a company? You can't go back to freelancer.",
    convertida: 'Your account is now a company.',
    empresaSalva: 'Company details updated.',

    idioma: 'Language',
    idiomaAjuda: 'Applies to the interface and to the AI analysis. Prices stay in US dollars.',
    idiomaSalvo: 'Language updated.',

    uso: 'Usage',
    projetos: 'Projects',
    paginasEVideos: 'Pages and videos',
    detalhePaginas: '{sites} site pages · {youtube} YouTube videos',
    melhorarPara: 'Upgrade to {plano}',
    falarConosco: 'Talk to us',

    creditos: 'AI credits',
    restantesNoMes: 'remaining this month',
    esteMes: '{n} this month',
    comprados: '{n} purchased',
    comprarChamada:
      "Need more this month? Purchased credits don't expire — they stay until you use them.",
    comprarPacote: 'Buy {n} credits',
    abrindo: 'Opening…',
    pagamentoRecebido: 'Payment received. Your credits will appear in a moment.',
    reiniciaEm: 'Resets on {data}',
    tokensPorAnalise: '{n} tokens per analysis',

    conector: 'AI connector',
    conectorIntro:
      'By default, the connected AI only reads your data. Here you can also let it request crawls.',
    conectorEscrita: 'Let the connected AI request crawls',
    conectorNotaCota: 'Each crawl consumes your plan page quota, and that is not reversible.',
    conectorNotaLimite:
      'There is a daily cap on crawls through this route, and one at a time per project. There is no such limit from the Silent Puma screen.',
    conectorNotaEscopo:
      'Nothing else changes: the AI still cannot create projects, delete runs, generate analyses or alter your account.',
    conectorReconectar:
      'Remove and add the connector again in your AI for this to take effect — it keeps the tool list from when you connected.',
    zonaDeRisco: 'Danger zone',
    riscoBasic: 'Deleting your account removes every project, run and analysis. There is no undo.',
    riscoPago:
      'Canceling keeps your plan until the end of the period already paid. Deleting the account removes everything immediately, with no undo.',
    riscoAnalista: 'Deleting your account only removes you from the company: projects and runs stay with the team.',
    confirmarSaida: 'Delete your account and leave the company? This cannot be undone.',
    cancelarAssinatura: 'Cancel subscription',
    assinaturaEncerra: 'Subscription will end at the close of the current period.',
    apagarConta: 'Delete account',
    confirmarCancelamento:
      'Your subscription stays active until the end of the current billing period, then moves to Basic. Continue?',
    confirmarExclusao: 'Delete your account and everything in it? This cannot be undone.',
    contaApagada: 'Your account has been permanently deleted.',

    business: 'Business plan',
    businessChamada: "Tell us about your needs and we'll put together a proposal.",
    empresa: 'Company',
    mensagem: 'Message',
    enviarMensagem: 'Send message',
    enviando: 'Sending…',
    mensagemEnviada: "Message sent. We'll get back to you soon."
  },

  equipe: {
    titulo: 'Team',
    texto: 'People on the team use the same projects, quota and credits as the company.',
    textoAnalista: 'Only a manager invites people and cancels invitations.',
    textoFreelancer: 'Teams belong to companies. Convert the account to a company in the Company tab to invite people.',
    textoBasic: 'Inviting people starts on the Guru plan.',
    gestor: 'Manager',
    analista: 'Analyst',
    papel: 'Role',
    pendente: 'Pending invitation, valid until {data}',
    expirado: 'Invitation expired. Invite again to send a new link.',
    cancelar: 'Cancel invitation',
    confirmarCancelamento: 'Cancel the invitation for {email}? The link will stop working.',
    cancelado: 'Invitation cancelled.',
    convidarTitulo: 'Invite',
    convidarTexto:
      'The invitation goes by email and is valid for 7 days. Analysts work on the projects; managers also handle the plan, payments, integrations and the team.',
    enviar: 'Send invitation',
    enviando: 'Sending…',
    enviado: 'Invitation sent. The link is valid for 7 days.',
    conviteTitulo: 'Team invitation',
    chamada: 'Your team is already on Silent Puma.',
    aceitarTitulo: 'Join {organizacao}',
    aceitarTexto: 'The invitation is to join as {papel}. Create your access.',
    aceitar: 'Accept and sign in',
    aceitando: 'Signing in…',
    voce: '(you)',
    papelDe: 'Role of {nome}',
    papelMudado: 'Role changed.',
    remover: 'Remove',
    confirmarRemocao: 'Remove {nome} from the team? Their account is deleted; projects and runs stay with the company.',
    removido: 'Removed from the team.'
  },
  painel: {
    ola: 'Welcome back, {nome}!',
    chamada: "Let's improve your SEO performance today.",
    projetos: 'Projects',
    paginasNoMes: 'Pages this month',
    creditos: 'AI credits',
    ilimitado: 'unlimited',
    execucoesRecentes: 'Recent runs',
    verProjetos: 'View projects',
    semExecucoes: 'No runs yet.',
    criarProjeto: 'Create a project',
    paraComecar: 'to start one.',
    atalhoComecar: 'Getting Started',
    atalhoComecarTexto: 'Create your first project and run your first analysis.',
    atalhoComecarCta: 'Create Project',
    atalhoAnalise: 'Run Analysis',
    atalhoAnaliseTexto: 'Start a new run on a project you already have.',
    atalhoAnaliseCta: 'View Projects',
    atalhoPlano: 'Plan and Usage',
    atalhoPlanoTexto: 'Pages, AI credits and what your plan still covers.',
    atalhoPlanoCta: 'Check Usage'
  },

  execucao: {
    coluna: 'Run',
    projeto: 'Project',
    paginas: 'Pages',
    videos: 'Videos',
    status: 'Status',
    iniciada: 'Started',
    acoes: 'Actions',
    limite: 'limit',
    concluida: 'Completed',
    emAndamento: 'Running',
    naFila: 'Queued',
    falhou: 'Failed',
    cancelada: 'Cancelled',
    verAnalise: 'View analysis',
    otimizacoes: 'AI Optimizations',
    relatorio: 'Report',
    detalhes: 'Details',
    repetir: 'Re-run',
    repetindo: 'Starting…',
    repetirAjuda: 'Crawl the same pages again, to compare',
    mesmasPaginas: 'Same pages as run #{n}',
    novaExecucao: '+ New Run',
    buscar: 'Search runs...',
    filtrar: 'Filter by status',
    total: { um: '{n} run', outros: '{n} runs' },
    retencao: '{n}-day retention',
    semExpiracao: 'no data expiration',
    vazio: 'This project has no runs yet.',
    semFiltro: 'No run matches the current filter.',
    primeira: 'Start the first run',
    confirmarExclusao: 'Are you sure you want to delete this run?',
    parar: 'Stop',
    confirmarParada: 'Stop this run now? Pages already collected are kept, but it will not get a score.',
    apagar: 'Delete run {n}'
  },

  projeto: {
    titulo: 'My Projects',
    total: { um: '{n} project total', outros: '{n} projects total' },
    vagas: '{usados} of {limite} slots on the {plano} plan',
    buscar: 'Search projects...',
    filtrar: 'Filter by type',
    site: 'Website',
    youtube: 'YouTube',
    paginas: 'pages',
    videos: 'videos',
    execucoes: 'runs',
    ultimaExecucao: 'last run',
    verExecucoes: 'View Runs',
    baixarCsv: 'Download CSV',
    painel: 'Dashboard',
    novo: 'New project',
    primeiro: 'Create your first project',
    tipoDeProjeto: 'Website or YouTube channel.',
    vagasLivres: {
      um: '{n} slot left on the {plano} plan.',
      outros: '{n} slots left on the {plano} plan.'
    },
    criar: '+ Create Project',
    confirmarExclusao: 'Are you sure you want to delete this project?',
    apagar: 'Delete {nome}',
    integracoesDe: 'Integrations for {nome}',
    integracoesChamada: "Connect Google data to enrich this project's analyses.",
    soNoPro: 'Available on Pro',
    soNoProTexto:
      'Integrations with Google Analytics, Search Console and YouTube Analytics are part of the Pro and Business plans.',
    verPlanos: 'See plans',
    hoje: 'today',
    ontem: 'yesterday',
    diasAtras: '{n}d ago',
    mesesAtras: '{n}mo ago',
    anosAtras: '{n}y ago'
  },

  paginacao: { anterior: 'Previous', proxima: 'Next' },

  site: {
    idioma: 'Language',
    recursos: 'Features',
    precos: 'Pricing',
    blog: 'Blog',
    contato: 'Contact',
    abrirMenu: 'Open menu',
    entrar: 'Log in',
    experimentar: 'Try It!',
    produto: 'Product',
    empresa: 'Company',
    legal: 'Legal',
    privacidade: 'Privacy Policy',
    termos: 'Terms of Service',
    direitos: '© {ano} Silent Puma. All rights reserved.'
  },

  /* Title e description das páginas públicas — ver src/config/seo.js. */
  seo: {
    homeTitulo: 'Silent Puma — SEO audit for websites and YouTube channels',
    homeDescricao:
      'Crawl your site, see what is broken ranked by impact and get AI recommendations. SEO audit for websites and YouTube channels, free up to 3,000 pages a month.',
    blogTitulo: 'Blog: what we measure, and what it means',
    blogDescricao:
      'Technical notes on SEO auditing from people who run crawlers every day. Each post carries the method, so you can check it instead of taking it on faith.',
    planosTitulo: 'Plans and pricing: start free',
    planosDescricao:
      'Compare Silent Puma plans. Basic is free: 1 project, 3,000 pages a month, no credit card. Paid plans add projects, pages, data retention and AI credits.',
    privacidadeTitulo: 'Privacy Policy: what we collect and why',
    privacidadeDescricao:
      'What Silent Puma collects, what the crawler collects, how AI analysis and Google integrations use your data, how long it is kept and what your rights are.',
    termosTitulo: 'Terms of Service',
    termosDescricao:
      'The terms for using Silent Puma: your account, what you may crawl, plans, limits and credits, payments and cancellation, AI recommendations and data retention.',
    entrarTitulo: 'Log in to your SEO dashboard',
    entrarDescricao:
      'Log in to Silent Puma to see your projects, crawl runs, SEO scores and AI recommendations for your websites and YouTube channels.',
    cadastroTitulo: 'Create your free account',
    cadastroDescricao:
      'Create a free Silent Puma account: 1 project and 3,000 pages a month, CSV export from day one and no credit card. Upgrade only when you need more.'
  },

  concluir: {
    titulo: 'Finish signing up',
    texto: 'Stripe confirmed your plan. Now set the password you will sign in with.',
    textoGoogle: 'Stripe confirmed your plan. The account is linked to Google: you sign in with the Google button, no password.',
    conferindo: 'Checking the confirmation from Stripe…',
    regrasTitulo: 'The password needs:',
    atendida: 'met',
    contaExiste: 'This account has already been created.',
    formato: 'How will you use Silent Puma?',
    freelancer: 'Freelancer',
    empresa: 'Company',
    equipeNasConfiguracoes: 'After signing up, invite your team in Settings, from the Guru plan up.',
    nomeDaEmpresa: 'Company name',
    funcionarios: 'Number of employees',
    tipoEmpresa: 'Type of company',
    selecione: 'Select',
    ate10: 'Up to 10',
    de11a50: '11 to 50',
    de51a200: '51 to 200',
    de201a1000: '201 to 1,000',
    maisDe1000: 'More than 1,000',
    agencia: 'Agency, serving clients',
    padrao: 'Company, managing its own brand',
    faltaFormato: 'Choose how you will use Silent Puma.',
    faltaEmpresa: 'Enter the company name, number of employees and type.'
  },

  juridico: {
    nestaPagina: 'On this page',
    emResumo: 'In short',
    traducao: 'Translation of the English text, which prevails if the two versions differ.',
    lerEmIngles: 'Read in English',
    leiaTambem: 'See also:',
    copiarSecao: 'Copy link to this section'
  },

  /* Cookie notice — components/AvisoDeCookies.js. */
  cookies: {
    titulo: 'Cookies',
    texto: 'With your permission, we use Google Analytics to see which pages get visited. Without it, nothing is sent to Google.',
    saibaMais: 'Learn more',
    aceitar: 'Accept',
    recusar: 'Decline'
  },

  naoEncontrada: {
    titulo: 'Page not found',
    ajuda: 'The page you are looking for does not exist or has been moved.',
    paraPainel: 'Go to my dashboard',
    paraHome: 'Back to home',
    meusProjetos: 'My projects'
  },

  acesso: {
    provaRetencao: "No crawl data is deleted before your plan's retention window.",
    runFeita: 'done',
    runRodando: 'running',
    runFalhou: 'failed',
    pontoBasico: '1 project and 3,000 pages per month',
    pontoBasicoSub: 'No credit card, no expiry date.',
    pontoCsv: 'CSV export from day one',
    pontoCsvSub: 'The data is yours, on every plan.',
    pontoRetencao: '7-day retention on Basic',
    pontoRetencaoSub: '30 days on Guru, 90 on Pro, unlimited on Business.',
    pontoUpgrade: 'Upgrade without losing history',
    pontoUpgradeSub: 'Old runs stay available when you change plan.',

    email: 'Email',
    senha: 'Password',
    mostrar: 'Show',
    esconder: 'Hide',
    ou: 'OR',
    comGoogle: 'Continue with Google',
    continuar: 'Continue',

    chamadaLogin: 'Your projects and runs are where you left them.',
    entrar: 'Sign in',
    entrarTexto: 'Sign in to see your projects and runs.',
    entrando: 'Signing in…',
    semConta: "Don't have an account?",
    criarConta: 'Sign up',
    esqueceu: 'Forgot password?',

    chamadaCadastro: 'Start on the free plan, without a card.',
    criarTitulo: 'Create your account',
    criarTexto: '1 project and 3,000 pages per month, free.',
    nomeCompleto: 'Full name',
    senhaDepois: "You'll choose a password on the next step, after confirming your plan.",
    criando: 'Creating…',
    criarBotao: 'Create account',
    jaTemConta: 'Already have an account?',
    consentimentoA: 'By creating an account you agree to our',
    consentimentoE: 'and our',

    chamadaEsqueci: 'Back in, in one email.',
    esqueciTitulo: 'Forgot password?',
    esqueciTexto: "Enter the email on your account and we'll send a reset link.",
    enviando: 'Sending…',
    enviarLink: 'Send reset link',
    linkEnviado: 'If this email is registered, a reset link is on its way.',
    lembrou: 'Remembered it?',

    chamadaNovaSenha: 'Set a new password and pick up where you stopped.',
    novaSenhaTitulo: 'New password',
    minimoDeCaracteres: 'At least {n} characters.',
    novaSenha: 'New password',
    confirmarSenha: 'Confirm password',
    salvando: 'Saving…',
    atualizarSenha: 'Update password',
    senhaAtualizada: 'Password updated. Taking you to sign in…',
    voltarAoLogin: 'Back to sign in',
    senhasDiferentes: 'Passwords do not match.',
    senhaCurta: 'Password must be at least {n} characters long.',

    chamadaConcluir: 'One step left — then your first crawl.',
    concluirTitulo: 'Complete your account',
    concluirTexto: "Payment confirmed. Set up how you'll sign in.",
    seuNome: 'Your name',
    seuEmail: 'Your email',
    escolhaSenha: 'Choose a password',
    regrasAtendidas: '{n} of {total} password rules met',
    aindaFalta: 'Still needs {itens}.',
    senhaPrecisa: 'Password needs {itens}.',
    contaGoogle: 'Account linked to Google — sign in with the Google button, no password needed.',
    concluindo: 'Finishing…',
    concluirBotao: 'Complete registration',
    regraTamanho: 'at least 8 characters',
    regraMaiuscula: 'an uppercase letter',
    regraMinuscula: 'a lowercase letter',
    regraNumero: 'a number',
    regraEspecial: 'a special character (!@#$%^&*)',

    autenticando: 'Authenticating…'
  },

  entrada: {
    titulo: 'Sign in to Silent Puma',
    texto: 'Your projects and runs are where you left them.',
    comGoogle: 'Continue with Google',
    abrindoGoogle: 'Opening Google…',
    ouEmail: 'or with email and password',
    email: 'Email',
    senha: 'Password',
    esqueceu: 'Forgot password?',
    mostrar: 'Show',
    esconder: 'Hide',
    botao: 'Sign in',
    entrando: 'Signing in…',
    faltaEmail: 'Enter your account email, like name@company.com.',
    faltaSenha: 'Enter your password. If you signed up with Google, use the Google button above.',
    erroDados: "Email or password doesn't match. If you signed up with Google, use the Google button above.",
    erroMuitas: 'Too many attempts in a row. Wait a few minutes and try again.',
    erroRede: "We couldn't reach the server. Check your connection and try again.",
    erroGoogle: "Signing in with Google didn't finish. Try again, or sign in with email and password.",
    semConta: "Don't have an account?",
    criarConta: 'Create a free account',
    novoNoBlog: 'New on the blog: {titulo}',
    fotoAlt: 'A mountain lion walking toward the camera at night, photographed by a camera trap.',
    fotoCredito: 'Photo: National Park Service, public domain'
  },

  recuperar: {
    titulo: 'Reset your password',
    texto: "Enter your account email and we'll send you a link to set a new password.",
    botao: 'Send the link',
    enviando: 'Sending…',
    enviado: "If there's an account for {email}, the link is on its way.",
    validade: "It works for 1 hour, and only once. If it doesn't arrive in a few minutes, check your spam folder.",
    deNovo: 'Send it again',
    lembrou: 'Remembered your password?',
    entrar: 'Sign in'
  },

  cadastro: {
    titulo: 'Create your Silent Puma account',
    texto: '{projetos} and {paginas} pages a month on the Basic plan, free and with no card.',
    projetos: { um: '{n} project', outros: '{n} projects' },
    ouEmail: 'or with name and email',
    nome: 'Name',
    depois: 'Next, Stripe confirms the Basic plan without asking for a card, and you come back to Silent Puma to set your password.',
    botao: 'Create free account',
    abrindoStripe: 'Opening Stripe…',
    consentimentoA: 'By creating an account, with Google or with email, you agree to the',
    consentimentoE: 'and the',
    jaTemConta: 'Already have an account?',
    entrar: 'Sign in',
    faltaNome: 'Enter your name.',
    faltaEmail: 'Enter an email like name@company.com.',
    emailExiste: 'That email already has an account.',
    irEntrar: 'Sign in with it',
    ou: 'or',
    irSenha: 'reset the password',
    erroCheckout: "We couldn't open Stripe right now. Try again in a moment.",
    semContaGoogle: "That Google account isn't registered with Silent Puma yet. Create yours here.",
    planoTitulo: 'What the Basic plan includes',
    planoProjetos: 'Projects',
    planoPaginas: 'Pages per month',
    planoHistorico: 'Run history',
    planoDias: { um: '{n} day', outros: '{n} days' },
    planoCreditos: 'AI credits per month',
    planoPreco: 'Price',
    planoGratis: 'Free, no card'
  },

  notas: {
    titulo: 'What the numbers mean',
    chamada:
      'Technical notes from people who run a crawler every day. Each post shows the method, so you can check it instead of taking our word for it.',
    minutos: { um: '{n} min read', outros: '{n} min read' },
    lerPost: 'Read the post',
    anteriores: 'Earlier posts',
    semPosts: 'Nothing published yet. The first notes are being written.',
    figuraTitulo: 'The five weights of the score, in points',
    figuraRotulo: 'The five weights of the score, in points: {lista}.',
    figuraLegenda: 'Each bar is as tall as the area is worth. Together they make the 100 points the score starts from.',
    trilha: 'Breadcrumb',
    nestePost: 'In this post',
    compartilhar: 'Share',
    copiarLink: 'Copy link',
    linkCopiado: 'Link copied',
    naoCopiou: "Couldn't copy. Copy the address from the browser bar.",
    legendaTela: "The cover of a sample run. Each bar is as tall as the area's weight; the empty top is what it lost.",
    calcTitulo: 'Do the math',
    calcTexto: 'Say how many pages each area affects and watch the score come out of the same function the product uses.',
    calcPaginas: 'Pages crawled',
    calcSemNota: 'No pages, no score',
    leiaTambem: 'Read next',
    fimBlog: 'See the math for your site',
    fimPost: 'See this math on your site',
    fimBotao: 'Crawl my site for free',
    fimNota: '{paginas} pages a month on the Basic plan, no card.'
  },

  home: {
    selo: 'AI-powered SEO analysis',
    tituloA: "Discover Your Website's ",
    tituloEm: 'Hidden Potential',
    chamada:
      'Silent Puma is your AI-powered SEO companion. It crawls your site, ranks what is broken, and hands you the list your client will ask about.',
    comecarGratis: 'Start Analyzing Free',
    semCartao: 'No card for the Basic plan',
    paginasGratis: '3,000 pages/month free',

    sinaisPorPagina: 'Signals per page',
    secoesDeAnalise: 'Analysis sections',
    siteEYoutube: 'Web and YouTube',

    demoPaginas: 'pages',
    demoScore: 'score',
    demoCriticas: 'critical',
    demoRodando: 'crawling',
    demoProgresso: 'PROGRESS',
    demoTituloLongo: 'title 71 ch',
    demoSemMeta: 'no meta desc',
    demoOk: 'ok',
    demoLinksQuebrados: '3 broken links',

    recursosEyebrow: 'Features',
    recursosTituloA: 'Powerful Features for ',
    recursosTituloEm: 'Better SEO',
    recursosChamada:
      "Discover how Silent Puma can transform your website's performance with our comprehensive suite of tools.",
    rapidoTitulo: 'Fast Analysis',
    rapidoMeta: 'RUST CRAWLER · HEADLESS RENDER',
    rapidoTexto:
      'Analyze your website with unmatched speed and precision. Get actionable insights in record time with our cutting-edge crawling engine.',
    exportarTitulo: 'Data Export',
    exportarMeta: 'CSV · STREAMED, ANY RUN SIZE',
    exportarTexto:
      'Export results seamlessly in multiple formats. Share insights effortlessly with your team or clients through CSV, Excel, or Google Sheets.',
    graficosTitulo: 'Interactive Charts',
    graficosMeta: 'FILTER BY RUN, URL AND STATUS',
    graficosTexto:
      'Visualize your data with stunning interactive charts. Identify trends and opportunities with our intuitive visualization tools.',
    iaTitulo: 'AI Optimizations',
    iaMeta: 'PRIORITIZED BY IMPACT',
    iaTexto:
      'The AI reads your crawl and returns a prioritized list of fixes, each one showing how many pages it affects. Standard or deep analysis, on your credits.',

    contatoTitulo: 'Get in Touch',
    contatoChamada:
      "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    respostaEmUmDia: 'REPLY WITHIN 1 BUSINESS DAY',
    chatAoVivo: 'Live chat',
    chatPlanos: 'GURU AND PRO PLANS',
    conectorMcp: 'MCP connector',
    conectorPlanos: 'GURU, PRO AND BUSINESS PLANS',
    seuNome: 'Your Name',
    seuEmail: 'Your Email',
    suaMensagem: 'Your Message',
    nomePlaceholder: 'Your name',
    emailPlaceholder: 'you@company.com',
    mensagemPlaceholder: 'I want to track 4 client domains every month.',
    enviando: 'Sending…',
    enviarMensagem: 'Send Message',
    mensagemEnviada: 'Message sent. We reply within one business day.',
    mensagemFalhou: "We couldn't send that. Please try again in a minute."
  },

  inicio: {
    palco: {
      titulo: 'SEO audit that explains itself',
      chamada:
        'We crawl every page of your site or YouTube channel, give it a score from 0 to 100 and show where every point came from.',
      acao: 'Crawl my site for free',
      nota: '{paginas} pages a month on the Basic plan, no card.'
    },
    como: {
      titulo: 'From address to score',
      intro:
        'A run is a complete crawl of the site, from the first link to the last. Nothing is analyzed before it finishes: half a picture does not tell you which half was left out.',
      escopoTitulo: 'You choose the scope',
      escopoTexto:
        'Create the project, a site or a YouTube channel, and say how far the crawler goes. Before it starts, the estimate reads your sitemap and shows how many addresses are in, with examples.',
      escopoDominio: 'Every link on the domain',
      escopoCaminho: 'Only below the starting path',
      escopoSubdominios: 'With subdomains, which count toward the same quota',
      rastreadorTitulo: 'The crawler goes through',
      rastreadorTexto:
        'Written in Rust, it opens each page like a browser and records 20 signals from it, so what only builds on the client is read by what it shows, not by the empty HTML. It obeys robots.txt and counts how many pages it left out because of it.',
      fotografiaTitulo: 'A dated photograph stays',
      fotografiaTexto:
        'When it finishes, the run keeps the site as it was that day: a score, five areas and, on each page, what is missing. A March report still says in September what it said in March.',
      fotografiaCota: 'A page that answered without content does not count toward the quota',
      fotografiaRepetir: '"Re-run" crawls exactly the same pages',
      fotografiaHistorico: 'History from {min} to {max} days, or unlimited on Business'
    },
    tela: {
      rotulo:
        'Silent Puma screen with a completed run of {site}: score {nota} out of 100 across {paginas} pages, and the points each area lost.',
      site: 'Site',
      execucao: 'Run #{n} · {data}',
      enderecoInicial: 'Starting address',
      ateOnde: 'How far the crawler goes',
      seguirDominio: 'Follow every link on the domain',
      abaixoDoCaminho: 'Stay below the starting path',
      incluirSubdominios: 'Include subdomains',
      parametros: 'URL parameters',
      descartar: 'Drop: each page once',
      manter: 'Keep: each filter becomes a page',
      estimativa: 'The sitemap lists {total} addresses. {casam} match your filters.',
      iniciar: 'Start run',
      rastreando: 'Crawling',
      deTotal: 'of {total} pages',
      headless: 'Headless browser',
      javascript: 'JavaScript rendered',
      robots: '{n} addresses',
      robotsTexto: 'closed by robots.txt',
      deCem: 'out of 100',
      desde: '+{n} since run #{anterior}, on the same pages',
      pontosPerdidos: 'Points lost per area',
      semMeta: 'no meta description',
      tituloLongo: 'title with {n} characters',
      semH1: 'no H1'
    },
    areas: {
      titulo: 'Five areas, one sum',
      formulaAntes: 'The score starts at 100 and each area loses',
      formulaConta: 'weight × pages with a problem ÷ total',
      formulaDepois: 'A page missing both a title and an H1 counts once.',
      pontos: '{area}, {n} points',
      indexacao1: 'Answers with a 2xx status',
      indexacao2: 'Renders and delivers real content',
      indexacao3: 'Has a canonical tag',
      indexacao4: 'Comes first: if the crawler cannot reach the page, nothing else matters',
      conteudo1: 'Title, description and H1 present: that is what enters the score',
      conteudo2: 'Title measured against {min} to {max} characters',
      conteudo3: 'Description against {min} to {max}',
      conteudo4: 'Fewer than {n} words becomes a request for review, not a rule',
      experiencia1: 'Server response under {lenta} s; above {critica} s is critical',
      experiencia2: 'Viewport meta present',
      experiencia3: 'Page under {mb} MB',
      experiencia4: 'We measure the server, not LCP, and the dashboard says so',
      midia1: 'Every image with alt text',
      midia2: 'Images counted page by page',
      midia3: 'The list comes ready for whoever fills it in',
      links1: 'No broken links',
      links2: 'At least one internal link: a page with no links is an orphan',
      links3: 'Internal and external links counted per page',
      controle: {
        indexacao: 'Pages with an indexing problem',
        conteudo: 'Pages with a content problem',
        experiencia: 'Pages with an experience problem',
        midia: 'Pages with a media problem',
        links: 'Pages with a links problem'
      },
      deTotal: '{n} of {total}',
      fecho: 'The score is not proof. It is the map of where you lose the most points per page touched.',
      acao: 'See my site’s sum',
      statusDasPaginas: 'Page status',
      semCanonical: 'No canonical',
      naoRenderizou: 'Did not render',
      comoAparece: 'How it shows in search',
      serpTitulo: 'Pro X Running Shoes: Responsive Cushioning for Long…',
      caracteresAcima: '{n} characters, above {max}',
      semMetaBuscador: 'No meta description: the search engine picks a snippet from the page.',
      semH1: 'No H1',
      tempoDeResposta: 'Server response time',
      lentasAcima: 'Slow, above {s} s',
      semViewport: 'No viewport',
      imagensDaPagina: 'Images on a product page',
      semAlt: 'no alt',
      comAlt: 'has alt',
      paginasSemAlt: 'Pages without alt',
      linksQuebrados: 'Broken links',
      menuDoBlog: 'All three are in the blog menu, on {n} pages.',
      semLinkInterno: 'No internal link'
    },
    saidas: {
      titulo: 'What each run delivers',
      nome: {
        relatorio: 'Report',
        correcoes: 'AI fixes',
        naSuaIa: 'In your AI',
        planilha: 'Spreadsheet',
        canal: 'YouTube channel'
      },
      legenda: {
        relatorio:
          'Each completed run becomes a technical audit report to hand to a client. It prints to PDF through the browser, with searchable text.',
        correcoes:
          'The AI reads the crawl and returns 4 to 10 fixes, by impact and, within the same impact, lowest effort first. Each one says how many pages it touches.',
        naSuaIa:
          'The connector takes your crawls into Claude, ChatGPT or Cursor. By default it only reads, and reading costs no credits and no quota.',
        planilha:
          'Every page as CSV, each with what the crawler measured and the issues it has. The export streams, so it works at any size.',
        canal:
          'A project can also be a channel. The channel score has four areas: metadata {metadados}, engagement {engajamento}, performance {desempenho} and publishing {publicacao}.'
      },
      relTitulo: 'Technical audit report',
      relMeta: '{site} · run #{n} · {paginas}',
      resumo: 'Summary',
      relResumo:
        'Indexing is healthy: {pct}% of pages answer 2xx and almost all have a canonical. What costs the most points is content, with {n} product pages without a meta description.',
      deOndeSairam: 'Where the points went',
      colArea: 'Area',
      colPeso: 'Weight',
      colPaginas: 'Pages',
      colPerdidos: 'Lost',
      total: 'Total',
      prioridades: 'Priorities',
      ia1: 'Write the meta description for the product pages',
      ia2: 'Replace the three blog menu links that lead to a 404',
      ia3: 'Give the category filter pages a canonical',
      ia4: 'Investigate the slow response of the category pages',
      ia5: 'Fill in the alt text of the product photos',
      iaTitulo: 'AI analysis',
      iaMeta: 'Standard · {custo} · version 2 · in English',
      iaResumo:
        'Indexing is healthy. Content weighs the most: the product pages were published without a description, and the search engine is picking snippets on its own.',
      mcpPergunta: 'Which pages on the store have a broken link?',
      mcpConsultou: 'Checked Silent Puma:',
      mcpExecucao: 'run #{n}',
      mcpResposta1: '{n} pages point to three addresses that answer 404, all in the blog sidebar menu:',
      mcpResposta2: 'Fixing the menu solves all {n} at once. Another {m} pages get no internal link at all.',
      mcpRodape: 'Read-only connector: no tool creates projects, deletes runs or touches your account.',
      canalMeta: 'YouTube channel · {videos} videos · run #{n}',
      video1: 'How to choose shoes for long races',
      video2: '20-minute interval workout',
      video3: 'Unboxing: Pro X',
      tituloAcima: 'Title with {n} characters, above {max}',
      engajamentoAbaixo: 'Engagement of {n}%, below {min}%',
      descricaoCurta: 'Description shorter than {min} characters'
    },
    degraus: {
      titulo: 'Start on the first step',
      anual: 'Yearly, {n} months free',
      paraSempre: 'forever',
      comecarGratis: 'Start for free',
      assinar: 'Get {plano}',
      falar: 'Talk to us',
      publico: {
        Basic: 'To check your own site.',
        Guru: 'For people who look after a few sites.',
        Pro: 'For agencies with several clients.',
        Business: 'For operations with no page ceiling.'
      }
    },
    perguntas: {
      titulo: 'Straight answers',
      p1: 'What counts as a page?',
      r1: 'Every address crawled in the month. A page that answered 2xx but delivered no content, even on the second try, does not count toward the quota. The month resets on your billing date.',
      p2: 'Do you respect robots.txt?',
      r2: 'Yes. The crawler never goes behind a login or into a page the site asks not to be indexed, and the run tells you how many addresses were left out because of that.',
      p3: 'Does it read pages built with JavaScript?',
      r3: 'It does. Each page is rendered as in a browser, so what counts is what it shows, not the empty HTML it returns.',
      p4: 'Is the load time Core Web Vitals?',
      r4: 'No. We measure the server response, not Largest Contentful Paint. Above {lenta} s a page is slow; above {critica} s, critical.',
      p5: 'How much does an AI analysis cost?',
      r5: 'Standard costs one credit and deep costs two. It never runs on its own, only when you ask. Basic includes {basic} credits a month, Guru {guru} and Pro {pro}.',
      p6: 'What goes to the AI provider?',
      r6: 'Aggregate metrics and a sample of addresses. Never your account details or your credentials.',
      p7: 'Which AIs does the connector work with?',
      r7: 'Claude, ChatGPT and Cursor, on the Guru, Pro and Business plans. By default it only reads, and reading costs no credits and no quota.',
      p8: 'How do I compare two dates?',
      r8: 'Use "Re-run" on the runs list: it crawls exactly the pages of the previous run, so the score difference measures the site, not the sample.',
      p9: 'Can I hand the report to a client?',
      r9: 'Yes. Each completed run becomes a technical audit report, printed to PDF through the browser, with searchable text.',
      p10: 'Can I cancel anytime?',
      r10: 'Yes. Canceling keeps the plan until the end of the period already paid.'
    },
    contato: {
      titulo: 'Still have a question?',
      chamada: 'Write to us: we reply within one business day.',
      mensagem: 'Your message',
      mensagemExemplo: 'I want to track 4 client domains every month.',
      nome: 'Your name',
      email: 'Your email',
      emailExemplo: 'you@company.com',
      ouEscreva: 'Or write directly to',
      enviar: 'Send message',
      enviando: 'Sending…',
      enviada: 'Message sent. We reply within one business day.',
      falhou: "We couldn't send that. Please try again in a minute."
    },
    cabecalho: {
      navegacao: 'Main'
    },
    fecho: {
      navegacao: 'Page sections',
      comoFunciona: 'How it works',
      cincoAreas: 'The five areas',
      oQueSai: 'What you get',
      perguntas: 'Questions',
      chat: 'Live chat on the Guru and Pro plans',
      conta: 'Account',
      criarConta: 'Create a free account',
      fotoAlt: 'A mountain lion walking toward the camera at night, photographed by a camera trap.',
      fotoLegenda:
        'Santa Monica Mountains, April 11, 2011, 1:25 a.m. A camera trap records what passes, silently, with date and time.',
      fotoCredito: 'Photo: National Park Service, public domain, via'
    }
  },

  precos: {
    titulo: 'Choose Your Plan',
    chamada: 'Start optimizing your website today with our flexible plans',
    ciclo: 'Billing cycle',
    mensal: 'Monthly',
    anual: 'Annual',
    mesesGratis: '{n} months free',
    maisPopular: 'Most Popular',
    personalizado: 'Custom',
    porMes: '/mo',
    moedaSimbolo: '$',
    anualDetalhe: '/mo, billed yearly (${valor})',
    carregando: 'Loading…',
    faleConosco: 'Contact Us',
    comecar: 'Get Started'
  },

  plano: {
    basicDescricao: 'Perfect for getting started',
    basicDestaque: 'Site and channel dashboard',
    guruDescricao: 'Best for growing teams',
    proDescricao: 'For professional users',
    businessDescricao: 'For large businesses',
    guruDestaque: '30-day history and run-to-run comparison',
    proDestaque: 'Google Analytics, Search Console and YouTube Analytics integrations',
    businessDestaque: 'Everything in Pro',
    conectorMcp: 'MCP connector for your AI',
    suporteEmail: 'Email support',
    suporteChat: 'Chat support',
    suportePrioritario: 'Priority chat support',
    suporteDedicado: 'Dedicated support',
    projetos: { um: '{n} project', outros: '{n} projects' },
    projetosIlimitados: 'Unlimited projects',
    paginasPorMes: '{n} pages/month',
    creditosPorMes: '{n} AI credits/month',
    paginasIlimitadas: 'Unlimited pages/month',
    creditosIlimitados: 'Unlimited AI credits/month',
    retencao: '{n}-day data retention',
    retencaoIlimitada: 'Unlimited data retention'
  },

  planosTela: {
    publicoTitulo: 'Choose Your Plan',
    publicoChamada: 'Start optimizing your website today. Cancel whenever you want.',
    titulo: 'Your plan',
    gratuitoParaSempre: ', free forever',
    cobradoStripe: ', billed through Stripe',
    faleConosco: 'Contact us',
    comecar: 'Get Started',
    planoAtual: 'Current plan',
    cancelarAssinatura: 'Cancel subscription',
    melhorar: 'Upgrade',
    rebaixar: 'Downgrade',
    atual: 'current',
    assinaturaCancelada: 'Subscription canceled.',
    trocaRecebida: 'Plan change confirmed. Your new plan will show here in a moment.',
    cobrancasRecentes: 'Recent charges',
    historicoCompleto: 'Full history',
    basicSemCobranca: 'The Basic plan is free — there is nothing to charge.',
    semCobrancas: 'No charges recorded yet.'
  },

  pagamentos: {
    titulo: 'Billing history',
    planos: 'Plans',
    basicGratis: 'The Basic plan is free — nothing is charged.',
    todasCobrancas: 'Every charge Stripe has made on this account.',
    semCobrancasBasic: 'No charges on the Basic plan.',
    semCobrancas: 'No charges recorded yet.',
    verPlanos: 'See plans',
    data: 'Date',
    descricao: 'Description',
    valor: 'Amount',
    status: 'Status',
    recibo: 'Receipt',
    ver: 'View',
    pago: 'paid',
    pendente: 'pending',
    falhou: 'failed',
    assinatura: 'Silent Puma subscription'
  },

  criar: {
    titulo: 'Create New Project',
    chamada: 'Configure your new tracking project',
    nome: 'Project Name',
    nomePlaceholder: 'Ex.: My Channel or Site',
    tipo: 'Project Type',
    canalYoutube: 'YouTube Channel',
    site: 'Website',
    url: 'URL',
    urlYoutube: 'https://www.youtube.com/@...',
    urlSite: 'https://www.mysite.com',
    ajudaYoutube: 'Enter your YouTube channel URL',
    ajudaSite: 'Enter your website URL',
    criando: 'Creating…',
    criar: 'Create Project'
  },

  crawl: {
    carregando: 'Loading…',
    titulo: 'Run Configuration',
    chamada: 'Configure your tracking run for {nome}',
    tipoDeProjeto: 'Project Type',
    youtube: 'YouTube',
    site: 'Website',
    urlAlvo: 'Target URL',
    usoDoPlano: 'Plan Usage',
    itensUsados: '{usados} of {total} items used',
    ilimitado: 'Unlimited',
    nomeDaRun: 'Run Name',
    nomePlaceholder: 'Ex.: Daily Check or Weekly Analysis',
    nomeAjuda: 'Give your run a descriptive name to easily identify it later',
    limites: 'Crawl Limits',
    disponivel: '(Available: {n})',
    rastrearAte: 'Crawl up to',
    urls: 'URLs',
    limiteAjuda: 'Define the maximum number of pages to crawl in this run',
    tudoSite: 'Crawl the entire site',
    tudoCanal: 'Crawl the entire channel',
    tudoAjuda:
      'The crawl runs until it exhausts the site or your plan quota, whichever comes first.',
    tudoAjudaCanal:
      'The crawl runs until it exhausts the channel or your plan quota, whichever comes first.',
    tudoTeto: 'There is a safety cap of {n} pages per run, even on unlimited plans.',
    tudoEstimativa: 'Estimated {n} pages, and you have {d} available.',
    tudoEstimativaExcede:
      'Estimated {n} pages, more than the {d} available — the crawl will stop at your quota.',
    iniciar: 'Start Run',

    configuracao: 'Crawl Configuration',
    comportamento: 'Crawl Behavior',
    seguirTudo: 'Follow all links',
    ficarNoDiretorio: 'Stay in the current directory',
    incluirSubdominios: 'Include subdomains',
    seuDominio: 'your domain',
    ajudaDiretorio:
      'Only addresses under the starting path, on {raiz} or www.{raiz}. Use it to look at one section without crawling the rest.',
    ajudaSubdominio:
      'Adds any subdomain — blog.{raiz} and the like — on top of {raiz} and www.{raiz}. They all count towards the same page quota.',
    ajudaTudo: 'Follows every link found, as long as it stays on {raiz} or www.{raiz}.',

    parametros: 'URL Parameters',
    manterTodos: 'Keep all parameters',
    ignorarTodos: 'Ignore all parameters',
    personalizado: 'Custom configuration',
    ajudaIgnorar:
      'Everything after "?" is dropped, so the same page is crawled once. Safe default for most sites.',
    ajudaPersonalizado: 'Reserved for a per-parameter rule. It behaves like "Keep all" today.',
    ajudaManter:
      'Each parameter combination counts as a separate page. On a store with colour and size filters, one product can consume dozens of pages from your quota.',

    apenasUrls: 'Only crawl URLs',
    tipoDeComparacao: 'Match type',
    apenasPlaceholder: '{exemplo}  — leave empty to crawl the whole site',
    apenasAjuda: 'Separate terms with commas; a page matching any of them is crawled.',
    excluirUrls: 'Skip URLs containing',
    excluirPlaceholder: '/about-us, /privacy-policy',
    excluirAjuda: 'Exclusions win over the field above.',
    exclusoesComuns: 'Common exclusions',
    exclusoesAjuda: 'These pages rarely carry SEO value and consume your page quota.',

    estimando: 'Checking the site…',
    estimar: 'Estimate matching pages',
    estimativaAjuda:
      "Read from the site's sitemap. It is an estimate: sitemaps go stale, and pages can exist without being listed.",
    correspondem: {
      um: '≈ {n} page matches your filters',
      outros: '≈ {n} pages match your filters'
    },
    deUrlsNoSitemap: 'Out of {encontradas} URLs listed in {sitemaps}',
    umSitemap: 'the sitemap',
    variosSitemaps: '{n} sitemaps',
    sitemapTruncado: ' (the site has more than we read)',
    limiteCobreParte: ' Your limit of {n} covers part of it.',
    pisoTitulo: 'At least {n} pages match',
    pisoDetalhe:
      'This site has no sitemap, so this counts only the links on the home page. The real number is very likely higher.',
    reescritoDetalhe:
      "Out of {encontradas} URLs. This site's sitemap lists them under {host} — the platform it runs on — and the same addresses answer on your own domain, so we counted them there.",
    outroHostTitulo: "Can't estimate from this sitemap",
    outroHostDetalhe:
      "The sitemap lists {encontradas} URLs, but all of them are on {host} rather than on your project's domain — common when a store runs on a hosted platform. Your filters target the project domain, so the two don't line up. The crawl itself still follows the links it finds on your site.",
    nenhumaDetalhe:
      "No sitemap found, and the home page did not respond within the time limit, not even with an error. It may be a temporary issue on the site: try estimating again in a moment. If it keeps happening, check the project's address.",
    nenhumaBloqueado:
      'The home page responded {status}: the site is refusing our crawler, and the crawl will hit the same wall.',

    avancado: 'Advanced Settings',
    padroesPermitidos: 'Allowed URL Patterns (Regex)',
    remover: 'Remove',
    adicionarPadrao: 'Add Pattern'
  },

  filtro: {
    nenhum: 'no filters',
    paginacao: 'Pagination',
    paginacaoAjuda:
      'Pages 2, 3, 4… of a listing. They repeat the same products and multiply your page count.',
    busca: 'Search & sorting',
    buscaAjuda:
      'Search results and sorted or filtered listings. On a store with facets these can be thousands of URLs showing the same catalog.',
    conta: 'Cart & account',
    contaAjuda: 'Cart, checkout, login and account pages. They are not meant to be indexed.',
    blog: 'Blog',
    blogAjuda: 'Useful when you want to look at the catalog only.',

    contendo: 'containing',
    contendoExemplo: '/product, /category',
    contendoAjuda:
      'The term appears anywhere in the URL. Broadest option — "/p" also matches "/privacy-policy".',
    comecandoCom: 'starting with',
    comecandoExemplo: '/store, /catalog',
    comecandoAjuda: 'Reads from the domain onwards. Use it to crawl one section of the site.',
    terminandoCom: 'ending with',
    terminandoExemplo: '/p',
    terminandoAjuda:
      'The URL finishes with the term. This is how you isolate VTEX product pages with "/p" without catching "/privacy-policy".',
    igualA: 'equal to',
    igualExemplo: '/about, /contact',
    igualAjuda: 'Only these exact addresses are crawled.'
  },

  metrica: {
    titulo: 'Title',
    descricao: 'Description',
    h1: 'H1',
    thumbnail: 'Thumbnail',
    comAlt: 'With alt',
    semAlt: 'Missing alt',
    interno: 'Internal',
    externo: 'External',
    views: 'Views',
    curtidas: 'Likes'
  },

  secao: {
    vazioVideos: 'No videos collected in this selection.',
    vazioData: 'No video in this selection has a recorded publish date.',
    semCanonical: 'No canonical tag',
    semTitulo: 'No title',
    semDescricao: 'No description',
    semH1: 'No H1',
    tituloForaDaFaixa: 'Title out of range',
    descricaoForaDaFaixa: 'Description out of range',
    conteudoRaso: 'Thin content',
    semViewport: 'No viewport',
    semOgImagem: 'No og:image',
    ogImagemRepetida: 'Repeated og:image',
    ytSemDescricao: 'No description',
    descricaoCurta: 'Short description',
    tituloLongo: 'Long title',
    semThumbnail: 'No thumbnail',
    totalVideos: 'Videos',
    totalViews: 'Views',
    mediaPorVideo: 'Average per video',
    abaixoDaMediaNota: '{n} below average',
    maisVistos: 'Most viewed',
    taxaGeral: 'Overall engagement',
    taxaGeralNota: 'healthy from {n}%',
    engajamentoBaixo: 'Low engagement',
    maisEngajados: 'Most engaging',
    semData: 'Without date'
  },

  painelSite: {
    titulo: 'Site Analysis Dashboard',
    abaVisao: 'Overview',
    abaIndexacao: 'Indexing',
    abaConteudo: 'Content',
    abaExperiencia: 'Experience',
    abaMidia: 'Media',
    abaLinks: 'Links',
    abaTrafego: 'Search & traffic',
    filtros: 'Filters',
    tempoCarga: 'Load time',
    abaDados: 'Data',
    paginasRastreadas: 'Pages Crawled',
    cargaMedia: 'Average Load Time',
    tamanhoMedio: 'Average Page Size',
    score: 'SEO Score',
    scoreAjuda:
      'Starts at 100 and loses a share of each area weight in proportion to the pages affected: indexing 25, content 30, experience 20, links 15, media 10. It is only worth reading against another run of the same site — the report shows that comparison.',
    semTitulo: 'Missing Titles',
    semDescricao: 'Missing Descriptions',
    execucao: 'Run:',
    execucaoSemNome: 'Unnamed run',
    url: 'URL:',
    igual: 'Equals',
    contem: 'Contains',
    naoContem: 'Does not contain',
    comecaCom: 'Starts with',
    terminaCom: 'Ends with',
    urlPlaceholder: 'Enter URL...',
    status: 'Status:',
    todosStatus: 'All Status',
    status200: '200 - Success',
    status301: '301 - Moved Permanently',
    status302: '302 - Found',
    status400: '400 - Bad Request',
    status404: '404 - Not Found',
    status500: '500 - Server Error',
    tempoDeCarga: 'Load Time (ms):',
    limpar: 'Clear',
    semExecucoesConcluidas:
      'No completed run in this project. The dashboard measures one run at a time — run one to see the numbers.',
    paginasComErro: 'Pages with errors',
    paginasComErroAjuda:
      'Responses in the 4xx and 5xx ranges. Search engines do not index what they cannot open, and internal links pointing here waste crawl budget.',
    grafico: {
      desempenho: 'Performance',
      seo: 'On-page SEO',
      arquitetura: 'Content and architecture',
      cargaTitulo: 'Load time distribution',
      cargaNota: 'Colour is judgement: green within target, red above 3 seconds.',
      statusTitulo: 'HTTP status codes',
      statusNota: 'Across the responses in this run.',
      statusCentro: 'responses',
      problemasTitulo: 'SEO issues',
      problemasNota:
        'Critical first, then warnings, each group by volume; the bar is the share of crawled pages. Click a problem to see its pages in the table.',
      problemasVazio: 'No issues in the measured areas.',
      canonical: 'Canonical tag',
      coberturaTitulo: 'Meta tag coverage',
      coberturaNota:
        'Presence per tag. Canonical is here because it is what separates a duplicate page from a new one.',
      profundidadeTitulo: 'Click depth',
      profundidadeNota:
        'How many clicks from the starting URL to the page. From four on it gets hard to reach, for people and for crawlers alike.',
      profundidadeVazio:
        'This run recorded no depth. Repeats of runs older than this measure have no level to inherit.',
      profundidadeRaiz: 'start',
      acessoTitulo: 'Media and access',
      acessoNota: 'Three presences that decide whether the page is found and understood.',
      imagensComAlt: 'Images with alt text',
      comLinkInterno: 'Pages with an outgoing internal link',
      comViewport: 'Pages with a viewport tag',
      com: 'with',
      sem: 'without'
    }
  },

  painelYt: {
    titulo: 'YouTube Dashboard',
    execucao: 'Run:',
    execucaoNumero: 'Run {n}',
    buscarTitulo: 'Search by Title:',
    tituloPlaceholder: 'Enter title...',
    faixaViews: 'Views Range:',
    faixaCurtidas: 'Likes Range:',
    faixaComentarios: 'Comments Range:',
    filtros: 'Filters',
    videos: 'Videos',
    score: 'Channel score',
    scoreAjuda:
      'Starts at 100 and loses part of each area weight in proportion to the videos affected: metadata 35, engagement 30, performance 25, publishing 10. Only meaningful when read against another run of the same channel.',
    semExecucoesConcluidas:
      'No completed run in this project. The dashboard measures one run at a time — start one to see the numbers.',
    grafico: {
      porMesTitulo: 'Views per published video',
      porMesNota:
        'Average per video published that month, not the total: the total would reward the month with the most uploads.',
      porMesVazio: 'No video in this run has a publish date to group by month.',
      tagsTitulo: 'Tags with the most engagement',
      tagsNota:
        'What the channel engages on, not what it publishes about. The rate is aggregated, and a tag on a single video is left out.',
      tagsVazio: 'No tag repeats across two videos with views.',
      distribuicaoTitulo: 'Views distribution',
      distribuicaoNota: 'How many videos sit in each reach band.',
      maisVistosNota: 'The five with the widest reach in this run.',
      dispersaoTitulo: 'Views × likes',
      dispersaoNota:
        'A point above the trend is a video that engaged more than its reach would suggest.',
      dispersaoVazio: 'Too few videos with views to draw the cloud.',
      maisEngajadosNota: 'Likes and comments over views, per video.',
      atencaoTitulo: 'Points of attention',
      atencaoNota:
        'Sorted by volume. Tags are left out: YouTube says they play a minimal role in discovery.',
      atencaoVazio: 'No points of attention in the measured areas.',
      porDiaTitulo: 'Publishing by day of week',
      porDiaNota: 'Average views per video published that day, not the day total.'
    }
  },

  colunaYt: {
    legenda: 'Captions',
    comLegenda: 'yes',
    semLegenda: 'no',
    tags: 'Tags',
    comentarios: 'Comments',
    publicadoEm: 'Published at',
    videoId: 'Video ID',
    engajamento: 'Engagement',
    naoCurtidas: 'Dislikes',
    url: 'URL',
    canal: 'Channel',
    execucao: 'Run'
  },

  tabela: {
    colEstrutura: 'Structure',
    colAutor: 'Author',
    colAtualizado: 'Updated on',
    colDensidade: 'Content in main',
    colProduto: 'Product schema',
    estrutura: {
      semPreco: 'no price',
      semDisponibilidade: 'no availability',
      semMarca: 'no brand',
      semAvaliacao: 'no rating',
      ok: 'ok',
      semHeadings: 'no headings',
      semH1: 'no H1',
      multiplosH1: 'multiple H1',
      saltoDeNivel: 'level skipped'
    },
    min: 'Min',
    max: 'Max',
    selecionarColunas: 'Select columns',
    colunasVisiveis: 'Visible columns',
    selecionarTodas: 'Select all',
    baixarCsv: 'Download CSV',
    linhasPorPagina: 'Rows per page:',
    intervalo: '{de}-{ate} of {total}',
    colUrl: 'URL',
    colStatus: 'Status',
    colCarga: 'HTML response (ms)',
    colTamanho: 'Size (KB)',
    colScore: 'SEO Score',
    colTitulo: 'Title',
    colDescricao: 'Meta Description',
    colH1: 'H1 Tags',
    colLinksQuebrados: 'Broken links',
    colLinksQuebradosProprios: 'Broken links (own)',
    colCliques: 'Clicks',
    colImpressoes: 'Impressions',
    colCtr: 'CTR',
    colPosicao: 'Avg. position',
    colSessoes: 'Sessions',
    colConversoes: 'Conversions',
    colRejeicao: 'Bounce rate',
    colEngajamento: 'Engagement/session',
    colViewsPorSessao: 'Pages/session'
  },

  otim: {
    titulo: 'Recommended optimizations',
    execucao: 'Run: {nome}',
    carregando: 'Loading…',
    analisando: 'Analyzing…',
    gerar: 'Generate',
    gerarDeNovo: 'Generate again',
    custo: { um: '{n} credit', outros: '{n} credits' },
    comCusto: '{acao} ({custo})',
    padraoRotulo: 'Standard analysis',
    padraoDescricao: 'Good depth for routine checks and recurring runs.',
    profundaRotulo: 'Deep analysis',
    profundaDescricao: 'More reasoning on complex sites. Costs more credits.',
    foco: 'Focus for this analysis',
    opcional: '(optional)',
    focoExemploSite: 'e.g. prioritize product pages, ignore the blog, the site is mobile-first.',
    focoExemploYoutube:
      'e.g. this is a tutorial channel, I want more subscribers, focus on 2026 videos.',
    demora:
      'This takes a few minutes. Keep this page open — the result appears here when it is ready.',
    versoes: 'Versions of this run:',
    maisRecente: 'latest',
    semModelo: 'model not recorded',
    focoUsado: 'Focus requested for this version:',
    vazio: 'No optimizations have been generated for this run yet.',
    vazioAjudaSite:
      'The AI reads your crawl data and returns a prioritized list of what to fix first, with the number of pages affected.',
    vazioAjudaYoutube:
      'The AI reads your collected videos and returns a prioritized list of what to adjust first, with the number of videos affected.',
    resumo: 'Overview',
    impacto: '{nivel} impact',
    esforco: '{nivel} effort',
    oQueFazer: 'What to do:',
    geradoEm: 'Generated {data} · {tokens} tokens',
    voltar: 'Back to projects',

    nivelAlto: 'High',
    nivelMedio: 'Medium',
    nivelBaixo: 'Low',
    paginas: { um: '{n} page', outros: '{n} pages' },
    videos: { um: '{n} video', outros: '{n} videos' },

    catIndexacao: 'Indexing',
    catConteudo: 'Content',
    catExperiencia: 'Experience',
    catMidia: 'Media',
    catLinks: 'Links',
    catEstruturados: 'Structured data',
    catDesempenho: 'Performance',
    catMetadados: 'Metadata',
    catEngajamento: 'Engagement',
    catPublicacao: 'Publishing'
  },

  relatorio: {
    voltar: '← Back to runs',
    execucaoDoRelatorio: 'Run for this report',
    salvarPdf: 'Save as PDF',
    carregando: 'Loading…',
    tipo: 'Technical audit',
    seloGerado: 'Silent Puma · report generated on {data}',
    titulo: 'Technical audit report',
    tituloDocumento: 'Silent Puma — {projeto} — run {run}',
    opcaoExecucao: '#{id} — {nome} — {data}',
    semRunConcluida:
      'This project has no completed run yet. A report is built from a finished crawl —',
    comeceUma: 'start one',
    eVolte: 'and come back when it is done.',

    paginasAnalisadas: 'Pages analyzed',
    acessiveis: 'Reachable',
    score: 'SEO score',
    problemasEncontrados: 'Issues found',
    rastreadoEm: 'Crawled on',

    resumo: 'Summary',
    prioridades: 'Priorities',
    oQueFazer: 'What to do',
    onde: 'Where',
    alcance: 'Reach',

    metricas: 'Technical metrics',
    metrica: 'Metric',
    medido: 'Measured',
    referencia: 'Reference',
    veredicto: 'Verdict',
    tituloCanal: 'Channel audit',
    videosAnalisados: 'Videos analyzed',
    coletadoEm: 'Collected on',
    tag: 'Tag',
    videos: 'Videos',
    metodologiaColeta:
      'The collection covered {paginas} from the channel {alvo} on {data}, in run #{run}. The numbers in this report are a snapshot of that moment: views, likes and comments keep changing after it.',
    metodologiaFaixasCanal:
      'A title counts as long above {titulo} characters, which is where YouTube starts truncating it in search and suggestions. A description counts as short below {descricao} characters, the stretch that shows in search results. Engagement is likes plus comments over views, and {engajamento}% is the market convention for a healthy rate — a reference, not a target.',
    metodologiaCanalNota:
      'Tags are listed as repertoire, not as a problem to fix: YouTube itself states they play a minimal role in discovery. The tag table measures the engagement of the videos carrying each tag, which says something about the subject, not about the tag.',
    scoreCanal: 'Channel score',
    porAreaNotaCanal:
      'A video can appear in more than one area — the same video can lack a description and have low engagement.',
    scoreNotaCanal:
      'The score starts at 100 and loses a share of the weight of each area in proportion to the videos affected: metadata 35, engagement 30, performance 25, publishing 10. A video with three metadata defects counts once — it is one video to fix, not three.',
    vOk: 'ok',
    vAviso: 'review',
    vCrit: 'critical',

    linhaAcessiveis: 'Pages returning 2xx',
    linhaComTitulo: 'Pages with a title',
    linhaTituloNaFaixa: 'Titles within range',
    linhaComDescricao: 'Pages with a meta description',
    linhaDescricaoNaFaixa: 'Descriptions within range',
    linhaComH1: 'Pages with an H1',
    linhaComCanonical: 'Pages with a canonical tag',
    linhaComViewport: 'Pages with a viewport meta',
    linhaRespostaMedia: 'Average server response',
    linhaLentas: 'Pages slower than 3s',
    linhaPesoMedio: 'Average page weight',
    linhaComAlt: 'Images with alt text',
    linhaLinksQuebrados: 'Pages with broken links',
    linhaSemLinkInterno: 'Pages with no internal link',
    linhaSemRender: 'Pages that did not render',
    refCaracteres: '{min}–{max} characters',
    refAbaixoDe: 'under {n}',

    porArea: 'Issues by area',
    porAreaNota:
      'A page can appear in more than one area — the same page may be missing a title and loading slowly.',

    scoreEntreRuns: 'Score across runs',
    de100: 'out of 100',
    desdeRun: '{sinal}{n} since run #{id}',
    scoreDaBarra: 'Run #{id}: {score}',
    scoreNota:
      "The score starts at 100 and loses a share of each area's weight in proportion to the pages affected: indexing 25, content 30, experience 20, links 15, media 10. A page with three problems in one area counts once — it is one page to fix, not three.",
    mesmaAmostra:
      'This run repeated the exact pages of run #{id}, so the difference above reflects changes to the site — not a different sample.',
    amostraDiferente:
      'Note: run #{id} covered {anteriores} pages and this one covered {atuais}. Each run discovers pages from the entry URL, so a different page set can move the score on its own — use Re-run on the runs list to measure the same pages again.',

    profundidade: 'Navigation depth',
    nivel: 'Level',
    paginas: 'Pages',
    fatia: 'Share',
    paginaInicial: 'Entry page',
    cliquesDeDistancia: { um: '{n} click away', outros: '{n} clicks away' },
    profundidadeNota:
      'Depth is the shortest path from the entry page. Pages sitting four or more clicks deep are crawled less often by search engines and are harder for visitors to reach.',

    metodologia: 'Methodology',
    metodologiaCrawl:
      'Silent Puma crawled {paginas} of {alvo} on {data}, following the scope and limits configured for run #{run}. Each page was fetched once and measured as delivered by the server.',
    metodologiaFaixas:
      'Title and description ranges ({tituloMin}–{tituloMax} and {metaMin}–{metaMax} characters) are the windows that usually display without truncation in search results — Google does not publish an official length. The {carga}s response threshold is stricter than needed for Core Web Vitals on purpose: we measure server response, not Largest Contentful Paint.',
    metodologiaRender:
      'Pages that answered with a 2xx status but delivered no title, no H1 and no internal links are reported as "did not render". They point at infrastructure, not at missing content, and they are not counted against your plan quota.',
    semIa:
      'This report has no Summary or Priorities section because no AI analysis has been generated for this run. Those sections come from it.',

    duvidas: 'Questions about this report: {email}',
    confidencial: 'Confidential · client use',
    recorte: 'The first {n} of {total}. The full list is in the dashboard.',
    falhou: 'This section could not be loaded.',
    periodo: 'Period',
    canal: 'Channel',
    finalidade: 'Purpose',
    rastreador: 'Crawler',
    acesso: 'Access',
    regra: 'robots.txt rule',
    dominio: 'Domain',
    termos: 'Terms',
    antes: 'Before',
    agora: 'Now',
    pioresPaginas: 'The {n} pages with the lowest score',
    plano: {
      gsc: 'Available on the Pro and Business plans, with Search Console connected.',
      ga4: 'Available on the Pro and Business plans, with Google Analytics connected.',
      gscGa4: 'Available on the Pro and Business plans, with Search Console and Google Analytics connected.',
      youtube: 'Available on the Pro and Business plans, with YouTube Analytics connected.'
    }
  },

  runAnalise: {
    titulo: 'Run #{id} analysis',
    paginas: { um: '{n} page', outros: '{n} pages' },
    baixarCsv: 'Export CSV',
    baixarPdf: 'Download PDF report',
    score: 'SEO Score',
    de100: 'out of 100',
    variacao: '{sinal}{n} vs. run #{run}',
    paginasAnalisadas: 'Pages analyzed',
    verTodas: 'View all pages',
    puladasPeloRobots:
      '{n} were not crawled: the site robots.txt forbids it. If that is not intentional, that is where to allow it.',
    bloqueado: {
      titulo: 'This site did not allow crawling',
      comStatus:
        'The starting address answered {status}, so we never saw the site. There is no score and no diagnosis in this run — what appears below is only what the block page carried.',
      semStatus:
        '{n} of {total} pages were refused, so there was no site left to measure. This run has no score.',
      comoLiberar:
        'If the site is yours, allow the agent {agente} in your WAF or CDN and run it again. If it is not, there is nothing to do from here — and working around the protection is not something we do.'
    },
    problemas: 'Issues found',
    maiorParteEm: { um: '{n} issue in {area}', outros: '{n} issues in {area}' },
    semProblemas: 'No issues across the five areas.',
    respostaMedia: 'Average response',
    acimaDoLimite: { um: '{n} slow page', outros: '{n} slow pages' },
    analiseIa: 'AI analysis',
    tokens: { um: '{n} TOKEN', outros: '{n} TOKENS' },
    verOtimizacoes: 'View optimizations',
    gerarOtimizacoes: 'Generate optimizations',
    semAnalise: 'This run has no AI analysis yet. Generating one uses a credit.',
    ondeEstaoAsPerdas: 'Where the losses are',
    nenhumaPerda: 'No page has issues in the measured areas.',
    perda: {
      semTitulo: 'No title',
      semDescricao: 'No meta description',
      semH1: 'No H1',
      semAlt: 'Images without alt',
      semCanonical: 'No canonical',
      linksQuebrados: 'With broken links',
      lentas: 'Slow to load',
      semLinkInterno: 'No outgoing internal link',
      semViewport: 'No viewport'
    },
    canal: {
      views: 'Total views',
      likes: 'Total likes',
      comentarios: 'Comments per video',
      engajamento: 'Engagement rate',
      semVariacao: 'No change',
      varPct: '{sinal}{n}% vs. run #{run}',
      varPorVideo: '{sinal}{n} per video vs. #{run}',
      varPontos: '{sinal}{n} pp vs. #{run}',
      videos: { um: '{n} video', outros: '{n} videos' },
      tagsTitulo: 'Tags with the most engagement',
      tagsVazio:
        'No tag appears on two or more videos — without repetition there is nothing to compare.',
      tagsNota:
        'Aggregate rate of the videos carrying each tag, with the bar relative to the first. Only tags on two or more videos are listed.',
      medidas: 'Channel measures',
      comDescricao: 'Videos with a description',
      descricaoInformativa: 'Informative description',
      tituloNoLimite: 'Title within the limit',
      comThumbnail: 'Videos with a thumbnail',
      engajamentoBaixo: 'Videos with low engagement',
      refMinimo: 'at least {n} characters',
      refAte: 'up to {n} characters',
      refAcima: 'above {n}%',
      vazio: 'This run collected no videos.',
      rodapeRelatorio: 'Tags by engagement and the methodology are in the',
      rodape: 'Every video, with filters and sorting, is in',
      verVideos: 'all videos'
    },
    metricasTecnicas: 'Technical metrics',
    rodape: 'The other nine technical measures are in the',
    rodapeLink: 'full report'
  },

  canal: {
    titulo: 'Channel performance',
    intro: 'What the public API does not tell: which searches brought the traffic, and how much of the video was watched. A 28-day window, closed 4 days back so YouTube can consolidate.',
    naoConectado: 'This run has no performance reading. Connect YouTube Analytics in the project integrations and run again.',
    retencao: 'Average retention',
    retencaoAjuda: 'How much of the video was watched on average, weighted by views — a video with 4 views does not weigh the same as one with 40,000. It is the closest thing to thumbnail CTR the API offers: impressions and CTR are YouTube Studio measures and do not exist here.',
    tempoAssistido: 'Minutes watched',
    tempoAssistidoAjuda: 'Total watch time in the window. It weighs more in YouTube recommendation than the view count does.',
    inscritos: 'Subscribers in the period',
    inscritosAjuda: 'Gained minus lost across the videos with a reading. It separates the video that attracts from the video that converts.',
    termos: 'Search terms',
    teto: 'The YouTube API returns at most {teto} terms per query, with no pagination. The list below is the top by views — it is not the complete list.',
    tituloTermos: 'What they typed to get here',
    termosDoCanal: 'The API refused the per-video filter on this reading, so the terms are for the whole channel rather than a specific video.',
    semTermos: 'No search terms in the window.',
    tituloVideos: 'Retention by video',
    semVideos: 'No video with a performance reading in the window.',
    termo: 'Term',
    video: 'Video',
    views: 'Views',
    minutos: 'Minutes',
    retencaoCurta: 'Retention',
    duracaoMedia: 'Avg. duration',
    inscritosCurto: 'Subscribers'
  },

  intencao: {
    titulo: 'Search intent',
    intro: 'What Google shows in the top 10 for each query, grouped by what people want, and whether your page is the right kind.',
    ler: 'Read intent with AI (1 credit)',
    lerDeNovo: 'Read again (1 credit)',
    lendo: 'Reading…',
    naoLido: 'Not read for this run yet. It uses the Google top 10 the run already saved.',
    indisponivel: 'This run did not save the Google top 10. Next runs with Search Console will.',
    nota: 'The AI only groups the results and judges the kind of your page; percentages are counted over the top 10. It is a mobile snapshot at the time of the run.',
    termo: 'Query',
    googleMostra: 'Google shows',
    suaPagina: 'Your page',
    fatia: '{pct}% · {forma}',
    posicao: '#{pos}',
    foraDoTopo: 'outside the top 10',
    encaixe: {
      sim: 'right kind',
      parcial: 'partly',
      nao: 'wrong kind',
      sem_pagina: 'no page'
    }
  },
  canibalizacao: {
    titulo: 'Pages competing for the same query',
    intro: 'Queries where Google splits impressions between pages of your site, measured in Search Console.',
    resumo: '{n} queries in dispute.',
    risco: 'About {cliques} clicks at risk.',
    semDados: 'No Search Console data on the crawled pages, so there is no way to see them competing.',
    nenhum: 'No query has two pages really competing. Every query with demand has one page answering it.',
    acao: 'If the pages serve the same search, merge the content into the main page and redirect the other (301). If they serve different searches — a category and a product, for example — keep both and make clear which one answers the query: title, internal links and canonical pointing to it.',
    estimativa: 'Clicks at risk: what the other pages would earn if their impressions went to the main page, at the CTR your site gets in its position. It is an estimate, left blank when the site has no sample at that position. Queries with the brand name are left out: there Google shows several pages of the site on purpose.',
    termo: 'Query',
    paginas: 'Pages',
    principal: 'main',
    detalhe: '{imp} impr. · pos. {pos}',
    impressoes: 'Impressions',
    cliques: 'Clicks',
    emRisco: 'Clicks at risk',
    erro: 'Could not load the competing pages.'
  },
  oportunidades: {
    avaliar: 'Check relevance with AI (1 credit)',
    avaliando: 'Checking…',
    reavaliar: 'Check again (1 credit)',
    relevancia: 'Does the page deserve it?',
    rel: {
      alta: 'yes, needs work',
      media: 'partly',
      baixa: 'wrong page'
    },
    alvo: 'The position gain is estimated against position {alvo}.',
    semTopo: 'Outside terms containing your brand name, the site does not appear in the first positions for any search with meaningful demand — the measurement only starts at position {alvo}. That is why the position gain is estimated against it rather than against the top: it is the best position the site actually reaches today.',
    foraMarca: '{n} terms containing “{marca}” were left out: searching for your own brand is not an opportunity, and its CTR is too high to serve as a baseline for the rest.',
    foraCanibal: '{n} pairs were left out for competing on a term already counted on another page. The click would not come twice — but two pages on the same term is a problem in itself.',
    titulo: 'Opportunities',
    intro: 'Click gain from the site\'s own CTR curve.',
    semCurva: 'There is not enough search data on the crawled pages yet to measure how much your site converts at each position. Without that curve, any gain estimate would be a guess.',
    estimativa: 'These gains are an estimate, not a forecast. They come from the CTR your own site gets at each position, and the position gain is deliberately conservative — it keeps current impressions, which in practice grow as the page rises.',
    verCurva: 'See the site CTR curve',
    ocultarCurva: 'Hide the curve',
    ctrMedido: 'Measured CTR',
    nenhuma: 'No pair with a meaningful estimated gain. That is a good sign: what has demand is already converting close to what the site usually converts.',
    termo: 'Term',
    pagina: 'Page',
    acao: 'Action',
    acaoTexto: 'Rewrite title and description',
    acaoConteudo: 'Work on content and links',
    posicao: 'Position',
    impressoes: 'Impressions',
    ctrAtual: 'CTR',
    ganho: 'Est. gain',
    resumo: '+{ganho} clicks to gain across {n} page and term pairs.'
  },

  integracoes: {
    titulo: 'Integrations',
    chamada:
      'Connect Google Analytics, Search Console and YouTube Analytics to cross performance with what was crawled.',
    naoConectado: 'Not connected',
    ativo: 'active',
    faltaEscolher: 'property not chosen yet',
    conectadoEm: 'Connected on {data}',
    conectar: 'Connect',
    escolherPropriedade: 'Choose property',
    trocarPropriedade: 'Change property',
    desconectar: 'Disconnect',
    semPropriedades: 'The connected account has no property available for this service.',
    confirmarDesconexao:
      'Disconnect {nome} from this project? Access is revoked at Google and the chosen property is lost.',
    retornoOk:
      'Account connected. Now choose which property belongs to this project — without it there is nothing to query.',
    retornoErro: 'The connection with Google could not be completed. Try again.',
    nota: 'Read-only: the scopes we request are read scopes. Nothing is written to your Google account, and the connection can be revoked here at any time.',
    faltaEscolherCanal: 'channel not chosen yet',
    escolherCanal: 'Choose channel',
    trocarCanal: 'Change channel',
    semCanais: 'The connected account does not manage any YouTube channel.',
    retornoOkCanal: 'Account connected. Now choose which channel belongs to this project — without it there is nothing to read.',
    canalDivergente: 'The connected channel is not the one this run crawled, so no performance was read. Choose the right channel and run again.'
  },

  trafego: {
    ctr: 'CTR',
    rejeicao: 'Bounce',
    periodo: 'Search Console and Analytics · {periodo} · {dias} days, the window this run measured',
    cobertura: {
      titulo: 'Match with the crawler',
      nota: 'Check this before the numbers: if URLs do not match, a zero below looks like a measurement and is not.'
    },
    tom: { ok: 'good', warn: 'attention', crit: 'poor' },
    faixa: { ok: 'top 10', warn: 'page 2', crit: 'past page 2' },
    gsc: {
      rotulo: 'Before the click · Search Console',
      titulo: 'How the site is found',
      leituraTitulo: 'Shows up and does not convince',
      leituraNota: '100+ impressions and CTR below 1%: the problem is in the title and description.',
      leituraMedida: '{impressoes} · {ctr}%'
    },
    ga: {
      rotulo: 'After the click · Analytics',
      titulo: 'What the visit does',
      medias: 'Average bounce of {rejeicao}, {engajamento} of engagement and {paginas} pages per session. In GA4, bounce is the inverse of engaged sessions.',
      leituraTitulo: 'Gets visits and bounces',
      leituraNota: '20+ sessions and bounce of 70% or more: the problem is after the click.',
      leituraMedida: '{sessoes} · {rejeicao}%',
      maior: 'The highest bounce among pages with 20+ sessions is {pct}%.'
    },
    vazio: 'No page in this run.',
    naoMedido:
      'This run did not measure search and traffic. Connect Google Analytics and Search Console to the project — future runs will bring the join.',
    conectar: 'Set up integrations',
    casamento: 'Match',
    casamentoNota: '{casadas} of {total} crawled pages have Google data',
    completude: 'Completeness',
    completudeNota: '{casadas} of {total} Google URLs were crawled.',
    orfasEm: 'The other {n} are in Technical.',
    cliques: 'Clicks',
    impressoes: 'Impressions',
    sessoes: 'Sessions',
    usuarios: 'Users',
    engajadas: 'Engaged',
    somadosPorEntrada: 'summed by landing page',
    conversoes: 'Conversions',
    porEntrada: 'by landing page',
    porEvento: 'on the event page',
    posicao: 'Position',
    posicaoMedia: 'Average position',
    pagina: 'Page',
    paginasTitulo: 'Performance by page',
    tabelaNota: 'In funnel order: shows up, gets clicked, keeps the visit, converts.',
    tabelaLink: 'To filter and export, use the Pages tab.',
    porDia: {
      titulo: 'Clicks and sessions by day',
      nota: 'The whole site, day by day. Hover or tap a bar to read the day.',
      serie: '{serie} · {total} in the period · peak of {pico} on {dia}',
      dia: 'Day'
    },
    porCanal: {
      titulo: 'Sessions by channel',
      nota: '{total} sessions across the whole site, each counted once, by the source GA4 assigned. Sessions above add up the pages: there, a visit that sees three pages counts three times.'
    },
    /* The names GA4 gives its channels, as GA4 itself translates them. */
    canal: {
      'Organic Search': 'Organic Search',
      'Paid Search': 'Paid Search',
      Direct: 'Direct',
      'Cross-network': 'Cross-network',
      'Organic Shopping': 'Organic Shopping',
      'Paid Shopping': 'Paid Shopping',
      'Organic Social': 'Organic Social',
      'Paid Social': 'Paid Social',
      'Organic Video': 'Organic Video',
      'Paid Video': 'Paid Video',
      'Paid Other': 'Paid Other',
      Display: 'Display',
      Referral: 'Referral',
      Email: 'Email',
      Affiliates: 'Affiliates',
      SMS: 'SMS',
      Audio: 'Audio',
      'Mobile Push Notifications': 'Mobile Push Notifications',
      'AI Assistant': 'AI Assistant',
      Unassigned: 'Unassigned'
    },
    orfasTitulo: '{n} URLs Google knows and the crawl did not visit',
    orfasNota: '{verificadas} checked by HTTP request; {faltam} awaiting a check.',
    recuperacaoTitulo: 'A rule can bring some of them back',
    recuperacaoMedida:
      'Tested on {testadas} URLs, it worked on {recuperadas} ({acerto}%). Estimate: {estimadas} of the {afetadas} affected would come back.',
    recuperacaoParcial:
      'Do not apply the rule without checking the target: the share that does not recover would land on the same error page you are trying to remove. The redirect must return 404 for what does not exist.',
    classe: { soft404: 'soft 404', morta: 'dead', redirecionada: 'redirected', viva: 'alive', bloqueada_robots: 'blocked by robots' },
    acao: { soft404: 'They return 200 with an error page. Redirect to the equivalent or return 404/410.', morta: 'Correct behavior: Google removes them over time. Redirect with 301 only if there is an equivalent.', redirecionada: 'Update internal links and the sitemap to the final destination.', viva: 'They work and the crawler did not reach them: an internal link is missing or the page cap cut them.', bloqueada_robots: 'robots.txt closes this area and we did not check them: confirm the block is intentional.' },
    orfasNotaTodas: 'All {verificadas} checked by HTTP request.',
    col: { estado: 'Status', urls: 'URLs', fazer: 'What to do' }
  },

  social: {
    titulo: 'Social networks',
    resumo: '{sessoes} sessions · {conversoes}',
    conversoes: { um: '{n} conversion', outros: '{n} conversions' },
    naoColetado:
      'This run did not read social traffic. Runs without Google Analytics, or made before this measurement, do not have the data.',
    vazio: 'No visits from social networks in the period.',
    organico: 'Organic',
    pago: 'Paid',
    nota: 'Based on Google Analytics channel grouping. Links opened from WhatsApp often arrive without a source and show up as direct traffic, not social.',
    col: {
      rede: 'Network',
      canal: 'Type',
      sessoes: 'Sessions',
      engajamento: 'Engaged',
      conversoes: 'Conversions',
      paginas: 'Landing pages'
    }
  },

  serp: {
    titulo: 'Who shows up with you',
    intro: 'Domains in the mobile top 10, across the {n} terms measured',
    naoMedido:
      'This run did not read the Google results page. It needs Search Console connected and search volume.',
    falhou: 'not returned',
    foraDoTopo: 'outside the top 10',
    bloco: {
      paid: 'Ads',
      ai_overview: 'AI Overview',
      featured_snippet: 'Featured snippet',
      answer_box: 'Direct answer',
      popular_products: 'Products',
      shopping: 'Shopping',
      local_pack: 'Local pack',
      map: 'Map',
      people_also_ask: 'People also ask',
      images: 'Images',
      video: 'Videos',
      short_videos: 'Short videos',
      top_stories: 'Top stories',
      knowledge_graph: 'Knowledge panel',
      discussions_and_forums: 'Forums'
    },
    nota: 'The bar counts in how many of the {n} terms the domain shows up in the top 10. It is a snapshot from the day of the run; the position and blocks for each term are in the table above.',
    acima: '{bloco} shows up above the site in {n} of the {m} terms where it is in the top 10.',
    acimaUm: '{bloco} shows up above the site in the only term where it is in the top 10.',
    semConcorrentes: 'No domain in the top 10 of these terms.'
  },
  comparacao: {
    titulo: 'Since the previous run',
    intro: 'Against run {nome}, from {data}, on the {n} pages both runs measured.',
    introRerun: 'Re-run of run {nome}, from {data}: the same {n} pages, measured again.',
    foraDaConta: '{atual} pages only in this run and {base} only in the previous one are left out.',
    corte: 'One of the two stopped at the page limit: the comparison holds for the pages in common, not for the whole site.',
    semBase: 'There is no earlier completed run of this project to compare with yet.',
    semMudanca: 'No problem changed on the pages in common.',
    col: { problema: 'Problem', corrigidas: 'Fixed', novas: 'New' },
    nota:
      'A page counts as fixed when it had the problem in the previous run and not in this one, and as new the other way around. The screen filters do not apply here. Sites whose content changes on every visit may show changes even when nothing was edited.'
  },
  tendencias: {
    titulo: 'Trends',
    naoMedido:
      'This run did not compare terms. Runs without Search Console, or made before this measurement, do not have the data.',
    emAlta: 'Rising',
    novos: 'New',
    emQueda: 'Falling',
    vazio: 'No terms.',
    nota: 'Only terms with at least 30 impressions in one of the two windows are included. Google omits rare searches, so a new term may already have existed below that limit. When the whole site grows, almost every term rises with it: compare each change with the total.',
    sub: 'Impressions in this window, against the previous 28 days'
  },

  velocidade: {
    titulo: 'Page speed',
    naoMedido: 'This run did not measure page speed. Runs made before this measurement do not have the data.',
    medindo:
      'Measuring the speed of the main pages on Google. It takes a few minutes after the crawl ends, and this section updates on its own.',
    medindoCsv:
      'Page speed is still being measured on Google. A CSV downloaded now comes without PageSpeed, LCP, INP and CLS; in a few minutes this notice goes away and the file comes out complete.',
    naoMedida: 'Google could not measure this page.',
    tom: {
      bom: 'good',
      medio: 'needs improvement',
      ruim: 'poor'
    },
    nota: 'Scores: below 50 is poor, 50 to 89 needs improvement, 90 or more is good. "domain": the page does not have enough visits for field data of its own, and the card above applies.',
    col: {
      pagina: 'Page',
      nota: 'Score',
      lcp: 'LCP',
      inp: 'INP',
      cls: 'CLS',
      resposta: 'HTML response',
      peso: 'HTML size'
    },
    fontes: 'Crawler: every page, at the time of the run · PageSpeed: {n} most visited pages, mobile',
    frase: { lab: 'The lab gives {media}.', labHtmlRapido: 'The lab gives {media}, and the HTML is not what weighs.', labHtmlLento: 'The lab gives {media}, and the HTML takes {ms} ms on average.' },
    rotulo: { resposta: 'HTML response', lab: 'Lab score', campo: '{m} · field' },
    notaResposta: 'average of {n} pages · crawler',
    mediaDe: 'average of {n} pages',
    limiteDe: 'limit {valor}',
    dominio: { titulo: 'Domain Core Web Vitals', sub: 'Real Chrome visitors, 28 days. {k} of the {n} pages have no data of their own and get the domain\'s.', subTodas: 'Real Chrome visitors, 28 days.', lcp: 'LCP · largest visible element', inp: 'INP · response to click and tap', cls: 'CLS · layout shift', regua: '{m} {valor}, in the {faixa} range; the good limit is {limite}', propria: '{pagina} has its own data: LCP {lcp}, INP {inp}, CLS {cls}.', home: 'The home page' },
    html: { titulo: 'HTML response time', sub: 'Measured by the crawler on the {n} pages.', leituraTitulo: 'Cross reading', leitura: '{a} responds in {msA} and scores {notaA}. {b} responds in {msB} and scores {notaB}. The low score comes from what loads after the HTML: scripts, images, fonts.' },
    tabela: { titulo: 'The {n} measured pages', sub: 'Lab score, median of 3 measurements; a single page varies about 10 points between runs, the site average does not. Response and weight come from the crawler.', dominio: 'domain' }
  },
  termosPrincipais: {
    titulo: 'Top terms, one row each',
    intro:
      'The {n} terms with the most impressions in Brazil, with what Search Console measured, the phone position during the run and the demand in Google Ads.',
    grupo: {
      gsc: 'Search Console · 28 days',
      serp: 'Phone · during the run',
      ads: 'Google Ads'
    },
    col: {
      termo: 'Term',
      impressoes: 'Impressions',
      variacao: 'Change',
      cliques: 'Clicks',
      posicao: 'Position',
      celular: 'Phone today',
      volume: 'Searches/mo',
      captura: 'Capture',
      meses: '12 months',
      cpc: 'CPC (US$)'
    },
    blocos: { um: '{n} block above', outros: '{n} blocks above' },
    serie: '12 months: from {de} to {ate} searches per month',
    concorrencia: {
      LOW: 'low',
      MEDIUM: 'medium',
      HIGH: 'high'
    },
    nota:
      'Change in impressions against the previous 28 days; compare it with the whole site in Trends. Search Console position is the average over 28 days and all devices; "Phone today" is a snapshot of one search during the run. CPC and competition from Google Ads, via DataForSEO.'
  },
  historico: {
    titulo: 'Across runs',
    col: {
      execucao: 'Run',
      nota: 'Score',
      cliques: 'Clicks'
    }
  },

  termos: {
    titulo: 'Search terms',
    intro:
      'Which searches each page shows up for: one page and term pair per row.',
    naoColetado:
      'This run did not collect search terms. Runs made before this measurement do not have the data.',
    truncado:
      'Collection hit the per-run cap. These are the most clicked terms, not all of them — the rest was left out.',
    recorte: 'List scope',
    recorteAtaque: 'Within striking distance',
    recorteTodos: 'All',
    mostrando: 'Showing {n} terms within striking distance, out of {total} total.',
    vazio: 'No search terms in this run.',
    vazioAtaque:
      'No terms within striking distance in this run. Pick “All” to see the full list.',
    anonimizacao:
      'Google does not report low-volume queries. That is why the sum of impressions per term is always lower than the page total — it is not a collection failure.',
    volumeNota:
      'Searches in Brazil in {mes}, in the rounded ranges Google Ads reports (via DataForSEO). Capture is the share of those searches in which the site appeared: impressions in Brazil ÷ searches in the month. A dash means Google has no data or the term had fewer than 10 impressions.',
    col: {
      termo: 'Term',
      pagina: 'Page',
      impressoes: 'Impressions',
      cliques: 'Clicks',
      ctr: 'CTR',
      posicao: 'Position',
      volume: 'Searches/mo',
      captura: 'Capture'
    }
  },

  botsIa: {
    titulo: 'AI bots in robots.txt',
    intro:
      "What the site's robots.txt allows AI search engines. A site can be flawless and still be closed to them.",
    indisponivel:
      "Could not read the site's robots.txt in this run. Without it there is no way to state what is allowed.",
    erro: 'This run did not check robots.txt.',
    aviso: '{n} AI crawler(s) without access to the site: {nomes}.',
    resposta: 'Read to answer now',
    treino: 'Read to train models',
    semJuizo:
      'Blocking may well be the right call — we recommend nothing here, we only show the state. What is not advisable is a block nobody knows about.',
    estado: {
      livre: 'Allowed',
      restrito: 'Partial',
      bloqueado: 'Blocked'
    },
    contagem: { restrito: { um: '{n} partial', outros: '{n} partial' }, bloqueado: { um: '{n} blocked', outros: '{n} blocked' } },
    todosLivres: 'all allowed'
  },

  docs: {
    titulo: 'Documentation',
    chamada: 'How Silent Puma works, described from what the product actually does.',
    indice: 'Sections',

    comecarTitulo: 'Getting started',
    comecarA:
      'A project is one website or one YouTube channel. Inside it, each run is one crawl: a snapshot of that site on that day. You can have {projetos} project(s) on the {plano} plan.',
    comecarB:
      'The order is always the same — create the project, configure the run, wait for it to finish, then read the dashboard. Nothing is analyzed before a run completes, because a partial crawl produces a partial picture and there is no way to tell which half is missing.',

    rastreamentoTitulo: 'Configuring a crawl',
    rastreamentoA:
      'Crawl behaviour decides how far the crawler goes: follow every link on the domain, stay under the starting path, or include subdomains. Subdomains count against the same page quota, so it is the option that changes your usage the most.',
    rastreamentoB:
      'URL parameters are the other setting that moves the number. Keeping them means every filter combination is a separate page — on a store with colour and size, one product can consume dozens of pages. Dropping them crawls each page once.',
    rastreamentoC:
      'Before starting, use "Estimate matching pages". It reads the site\'s sitemap and shows how many addresses your filters match, with examples. The examples are the point: they are how you notice that "/p" also caught the privacy policy.',

    painelTitulo: 'Reading the dashboard',
    painelA:
      'The dashboard is split into five areas, in the order the problems matter: indexing, content, experience, media and links. If the crawler cannot reach a page, nothing else about that page matters — which is why indexing comes first.',
    painelB:
      'The number on each tab is how many pages have a problem in that area, not how many problems exist. A page missing both a title and an H1 counts once. A tab showing a check mark means there is nothing to fix there, so you can skip it without opening.',
    painelC:
      'Titles are measured against {tituloMin}–{tituloMax} characters and descriptions against {metaMin}–{metaMax}. Google publishes no official length; those are the ranges that usually display without truncation. Response time above {lenta}s is flagged as slow — we measure server response, not Largest Contentful Paint.',

    iaTitulo: 'AI analysis',
    iaA: 'The AI reads the crawl data and returns a prioritized list of fixes, each showing how many pages it affects. It never runs on its own: generating costs credits, so it only happens when you ask. Your plan includes {creditos} credits per month.',
    iaB: 'Standard analysis costs one credit and deep analysis costs two. Deep spends more reasoning on complex sites; on a routine check the difference rarely shows. The focus field is the control worth using — you are the one who knows that the blog does not matter or that the catalog is the priority.',
    iaC: 'Every generation is saved as a version, so you can compare two configurations without paying twice. The analysis is written in your account language, which you set in your profile.',

    conectorTitulo: 'Connector for your AI',
    conectorA:
      'The connector opens your crawl data inside the AI you already use — Claude, ChatGPT or Cursor. Instead of reading the report here, you ask there, with the model and the context that are already yours.',
    conectorB:
      "By default it only reads, and reading costs no credits and no quota, because the intelligence is yours. If you want, you can allow the AI to request crawls in Settings: each one uses your plan's page quota, with a daily cap and one at a time per project. Even then, no tool creates projects, deletes runs, generates analyses or changes your account.",
    conectorC:
      'Questions it answers well: which pages have broken links, which terms each page shows up for, what the last analysis recommended prioritising, how much of your plan you have used, and which channel videos have the most views.',
    conectorD:
      'When we publish new tools they do not appear on their own: your AI keeps the list from when you connected. Remove the connector and add it again to see them.',
    conectorPasso1: 'Copy the address above.',
    conectorPasso2:
      'In your AI, open the connector settings and choose to add a custom connector. In some apps the option is labelled "MCP".',
    conectorPasso3:
      'Paste the address and confirm. You will be brought back to Silent Puma to sign in and authorise access.',
    conectorPasso4:
      'That is it. Go back to the conversation and ask about your projects — the AI will fetch the data on its own when it needs to.',

    relatorioTitulo: 'The PDF report',
    relatorioA:
      'Each completed run can become a technical audit report, made to hand to a client. It opens as a page and prints through the browser, which produces real vector text — searchable and sharp at any zoom, unlike an image-based export.',
    relatorioB:
      "The report carries an SEO score from 0 to 100. It starts at 100 and loses a share of each area's weight in proportion to the pages affected: indexing 25, content 30, experience 20, links 15, media 10. A page with three problems in one area counts once — it is one page to fix, not three.",
    relatorioC:
      'When the AI analysis exists, the report opens with its summary and priorities. Without it the report still comes out, just without those two sections.',

    repetirTitulo: 'Repeating a run',
    repetirA:
      'Two runs of the same site rarely cover the same pages: each one discovers addresses from the entry URL, so a link that appeared this week changes the sample. Comparing scores across different samples measures the sample, not the site.',
    repetirB:
      '"Re-run", on the runs list, crawls exactly the pages the previous run collected. That is what makes the score difference mean something — and the report says which of the two situations you are in.',

    planosTitulo: 'Plans and limits',
    planosA:
      'Your plan sets three things: how many projects you can have ({projetos}), how many pages you can crawl per month ({paginas}), and how long the collected data is kept ({retencao} days). The month resets on your billing date.',
    planosB:
      'Pages that answered with a success status but delivered no usable content do not count against the quota. You did not receive data for them, and you still have the work of finding out why — charging quota on top of that would be charging for something not delivered.',

    dadosTitulo: 'Your data',
    dadosA:
      'Crawls collect what a page publishes: address, status, title, description, headings, links and image counts. Nothing behind a login, and no page the site asks not to be indexed.',
    dadosB:
      'When you ask for an AI analysis, aggregate metrics and a sample of addresses go to the AI provider — never your account details or your credentials. The privacy policy describes it field by field.',

    duvidaTitulo: 'Still stuck?',
    duvidaTexto:
      'Write to {email} and describe what you were doing and what you expected. Mentioning the run number helps — it lets us look at exactly the data you were looking at.'
  },

  blog: {
    eyebrow: 'Blog',
    titulo: 'What we measure, and what the numbers mean',
    chamada:
      'Technical notes from people who run crawlers every day. Each post carries the method, so you can check instead of believe.',
    buscar: 'Search the blog',
    emDestaque: 'Featured',
    todosOsPosts: 'All posts',
    publicados: { um: '{n} PUBLISHED', outros: '{n} PUBLISHED' },
    minutos: { um: '{n} min read', outros: '{n} min read' },
    semPosts: 'Nothing published yet. The first notes are being written.',
    semResultado: 'No post matches this search.',
    voltar: '← Back to the blog',
    nestePost: 'In this post',
    leiaTambem: 'Read next',
    catProduto: 'Product',
    catMetodo: 'Method',
    catYoutube: 'YouTube'
  },

  erros: {
    oportunidades: 'Could not load the opportunities.',
    desempenhoCanal: 'Could not load the channel performance.',
    perfil: 'Could not save your profile.',
    empresa: 'Could not save the company details.',
    convite: 'Could not send the invitation.',
    equipe: 'Could not change the team.',
    foto: 'Error updating profile photo.',
    uso: 'Error fetching usage data',
    checkout: 'Error creating checkout session',
    compra: 'Could not start the purchase.',
    cancelamento: 'Error canceling subscription.',
    exclusao: 'Error deleting account.',
    mensagem: 'Error sending message',
    planoIndisponivel: 'This plan is not available for purchase right now.',
    integracao: 'Could not start the integration.',
    integracaoRecursos: 'Could not list the properties of the connected account',
    projetos: 'Error loading projects',
    apagarProjeto: 'Error deleting project',
    baixar: 'Error downloading results',
    acessoNegado: 'Access denied to this project',
    carregarProjeto: 'Error loading project',
    carregarExecucoes: 'Error loading runs',
    trafego: 'Error loading search and traffic',


    termos: 'Error loading search terms',
    social: 'Error loading social network traffic',
    velocidade: 'Error loading page speed',
    tendencias: 'Error loading rising terms',
    comparacao: 'Error loading the comparison with the previous run',
    serp: 'Error loading the Google results page',
    execucao: 'Could not load the run',
    repetir: 'Could not start the re-run.',
    apagarExecucao: 'Error deleting run',
    pararExecucao: 'Could not stop the run.',
    login: 'Login failed. Please check your credentials.',
    loginFalha: 'Error logging in. Please try again later.',
    conexao: 'Connection error. Please try again.',
    emailUsado: 'Email already registered',
    cadastro: 'Error processing request.',
    recuperacao: 'Failed to process request.',
    atualizarSenha: 'Failed to update password.',
    concluirCadastro: 'Error completing signup.',
    plano: 'This plan is not available right now.',
    requisicao: 'Request processing error.',
    generico: 'Something went wrong. Please try again later.',
    cancelamentoAssinatura: 'Could not cancel the subscription.',
    historico: 'Error loading payment history',
    configuracao: 'Error loading configuration.',
    criarProjeto: 'Error creating project.',
    criarProjetoTentar: 'Error creating project. Please try again.',
    nomeDaRun: 'Please enter a name for the run.',
    semItens: 'You only have {n} items available in your {plano} plan.',
    iniciarCrawler: 'Error starting the crawler.',
    servidorInacessivel: 'Could not reach the server.',
    enviar: 'Could not send. Please try again.',
    execucoes: 'Failed to load runs data',
    dados: 'Error loading data',
    otimizacoes: 'Could not generate the optimizations.',
    projeto: 'Could not load this project.',
    execucoesDoProjeto: 'Could not load the runs for this project.',
    paginasColetadas: 'Could not load the collected pages.'
  },

  /* New signed-in area (NewAppShell and the New* screens). */
  area: {
    navegacao: 'Main navigation',
    caminho: 'Breadcrumb',
    de: 'of {total}',
    semLimite: 'unlimited',
    limiteDoPlano: 'plan limit of {total}',
    creditosRestantes: '{restantes} of {total} AI credits available',
    creditosDoMes: 'Monthly: {mes}',
    creditosOrigem: 'Monthly: {mes} · Purchased: {extra}',
    creditosCurto: 'credits',
    privacidade: 'Privacy',
    termos: 'Terms',
    hoje: 'today',
    ontem: 'yesterday',
    dias: { um: '{n} day ago', outros: '{n} days ago' },
    meses: { um: '{n} month ago', outros: '{n} months ago' },
    anos: { um: '{n} year ago', outros: '{n} years ago' },
    painel: {
      resumo: 'Account summary',
      ultima: 'Latest run: #{id}, on {projeto}, {situacao}.',
      situacao: {
        completed: 'started {quando}',
        in_progress: 'in progress',
        pending: 'queued',
        error: 'failed',
        cancelled: 'cancelled'
      },
      paginasEVideos: 'Pages and videos',
      paginasDetalhe: '{sites} from sites · {videos} from YouTube',
      atalhos: 'Shortcuts',
      atalhoDocsTexto: 'How the score is calculated and how to set up a crawl.'
    },
    integracoes: 'Integrations',
    intervalo: '{de}–{ate} of {total}',
    projetos: {
      tipo: 'Type',
      itens: 'Pages or videos',
      ultima: 'Latest run',
      paginas: { um: 'page', outros: 'pages' },
      videos: { um: 'video', outros: 'videos' },
      execucoes: { um: 'run', outros: 'runs' },
      semResultado: 'No project with that name in this filter.'
    },
    criar: {
      tipoTexto: 'Sets the address asked below and what the run collects: pages or videos.',
      nomeTexto: 'To find the project in the list and on the dashboard.'
    },
    sim: 'Yes',
    nao: 'No',
    conta: {
      usoEm: 'Usage, credits and plan changes are in',
      perfilTexto: 'Name and photo show in the menu. The email is the one you sign in with.',
      trocarFoto: 'Change photo',
      idiomaNaHora: 'Changes right away and is saved to the account.'
    },
    plano: {
      uso: 'Account usage',
      recurso: 'Feature',
      seuPlano: 'your plan',
      linhas: {
        preco: 'Price',
        projetos: 'Projects',
        paginas: 'Pages per month',
        creditos: 'AI credits per month',
        painel: 'Site and channel dashboard',
        conector: 'MCP connector for your AI',
        integracoes: 'Analytics, Search Console and YouTube Analytics',
        suporte: 'Support',
        retencao: 'Data retention'
      },
      dias: { um: '{n} day', outros: '{n} days' },
      ultimaRenovacao: 'Monthly credits renewed on {data}',
      ultimaCobranca: 'Latest charge',
      cobrancaFalhou: 'This charge did not go through. The history lists every charge.',
      mensagemBusiness: "I'm interested in the Business plan."
    },
    pagamentos: {
      verRecibo: 'View receipt',
      semRecibo: 'No receipt'
    },
    abas: {
      rotulo: 'Project pages',
      painel: 'Dashboard',
      visao: 'Overview',
      trafego: 'Search & traffic',
      termos: 'Keywords',
      tecnico: 'Technical',
      velocidade: 'Speed',
      paginas: 'Pages'
    },
    palavras: {
      fonte: { gsc: 'Search Console, queries in Brazil · {periodo}', volume: 'Google Ads volume for {mes}', serp: 'mobile SERP on {data}' },
      rotulo: { termos: 'Terms with impressions', ataque: 'In striking distance', celular: 'In the mobile top 10', impressoes: 'Impressions in Brazil' },
      nota: { termos: 'page × term pairs', celular: 'the {n} terms with the most impressions', impressoes: '{antes} → {agora}, against the previous 28 days' },
      de: 'of {n}'
    },
    visao: {
      ctrPosicao: '{ctr}% CTR · position {posicao}',
      conversoes: { um: '{n} conversion', outros: '{n} conversions' },
      ataque: 'position 5 to 20, with demand',
      velocidade: 'Speed score',
      naoMedido: 'not measured in this run',
      rotulo: { cliques: 'Google clicks', ataque: 'Terms in striking distance', cwv: 'Core Web Vitals' },
      critico: 'critical',
      nenhum: 'none',
      tipos: { um: 'in {n} type of issue', outros: 'in {n} types of issue' },
      engajadas: '{pct}% engaged',
      de3: 'of 3 good',
      cwvNota: 'LCP {lcp} · INP {inp}',
      contexto: 'Run #{id} · {data}',
      google: 'Google: {periodo}',
      frase: {
        paginas: '{n} pages, {erro} with errors.',
        google: 'On Google, {impressoes} impressions became {cliques} clicks.',
        cwvTodos: 'Real visitors pass all three Core Web Vitals.',
        cwvParte: 'Real visitors pass {n} of 3 Core Web Vitals.'
      },
      blocos: { um: '{n} block', outros: '{n} blocks' },
      achados: { titulo: 'One finding per tab', sub: 'the biggest of each topic in this run' },
      achado: {
        termo: '"{termo}" has {volume} searches a month and the site captures {captura}%.',
        celularPosicao: ' On a phone, it ranks {pos}, with {blocos} above.',
        celularFora: ' On a phone, it is outside the top 10, with {blocos} on the page.',
        pagina: '{pagina} shows up {impressoes} times at position {posicao} and gets {cliques} clicks ({ctr}% CTR).',
        orfas: '{soft} URLs Google still knows respond as soft 404, and {mortas} are dead.',
        velocidade: '{pagina} scores {nota} in the lab, the lowest of the {n} measured.',
        problema: 'The most frequent critical issue is "{problema}", on {n} pages.'
      },
      acao: { termo: 'See term', pagina: 'See page', urls: 'See URLs', lista: 'Open list' },
      fontes: {
        titulo: 'Sources in this run',
        crawler: 'Crawler',
        gsc: 'Search Console',
        ga: 'Analytics',
        psi: 'PageSpeed',
        dfs: 'DataForSEO',
        robots: 'robots.txt',
        paginas: '{n} pages',
        psiDetalhe: '{n} pages, phone',
        volume: 'volume for {mes}',
        bots: '{n} AI bots',
        lido: 'read',
        naoMedido: 'not measured',
        falhou: 'failed'
      },
      corrigidas: { um: '{n} fixed', outros: '{n} fixed' },
      novas: { um: '{n} new', outros: '{n} new' },
      saldo: 'Against #{base}, shared URLs only: {corrigidas} and {novas}.',
      detalhe: 'Details in Technical'
    },
    conjuntos: {
      rotulo: 'Column set',
      essencial: 'Essentials',
      tecnico: 'Technical',
      trafego: 'Traffic',
      conteudo: 'Content',
      produto: 'Product',
      oQueMostra: 'What each set shows'
    },
    runs: {
      repeteA: 'repeats #{n}'
    },
    nova: {
      titulo: 'New run',
      filtros: 'URL filters',
      filtrosTexto: 'To crawl only part of the site. Exclusions win over the include filter.'
    },
    site: {
      indice: 'On this page',
      resumo: 'Summary',
      tecnico: 'Technical',
      recorte: 'They apply to Technical and Pages. The other tabs show the whole run.',
      criticos: 'Critical issues',
      avisos: 'Warnings',
      cargaMedia: 'Average of {ms} ms across the {n} crawled pages.',
      links: {
        titulo: 'Broken links',
        com: 'Pages with a broken link',
        proprios: 'With an own broken link',
        template:
          'No page has an own broken link: it is the same layout link, repeated on all of them. Fixing the template solves the {n}.',
        proprio: 'To see which pages have an own broken link, use the "Broken links (own)" column in Pages.'
      },
      carga: 'HTML response (ms)',
      dasPaginas: '{pct}% of pages',
      paginas: { um: '{n} page', outros: '{n} pages' },
      aviso: 'warning',
      profundidadeMax: 'max depth {n}',
      de: '{a} of {b}',
      problemas: { titulo: 'Issues', nota: 'Severity first, then count. Clicking opens Pages filtered by the issue.', escala: 'bar = % of the {n} pages' },
      cartao: { metadados: 'Metadata', acesso: 'Accessibility and links', profundidade: 'Clicks from the home page', status: 'HTTP status' },
      paginasSub: 'Same cut as Technical. The CSV comes out with the filters, every column and the issues of each page.',
      problema: 'Issue',
      semProblema: 'No filter'
    },
    run: {
      numeros: 'Run numbers'
    },
    otim: {
      caminho: 'Optimizations',
      profundidade: 'Depth'
    },
    yt: {
      graficos: 'Charts',
      recorte: 'They apply to the summary, the charts and the table. Channel performance shows the whole run.'
    }
  }
};

export default en;
